import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import toast from "react-hot-toast";
import {useSelector} from "react-redux";
import {Menu, Popover} from "@headlessui/react";
import {AdjustmentsIcon, EyeIcon, EyeOffIcon, ExclamationCircleIcon} from "@heroicons/react/outline";
import {ChevronDownIcon} from "@heroicons/react/solid";

import DataTableEditable from "../../../components/table/data-table-editable";
import Loader from "../../../components/common/loader/loader";
import CloneIcon from "../../../components/icons/clone-icon";
import TrashIcon from "../../../components/icons/trash-icon";
import BoxIcon from "../../../resources/icons/BoxIcon";
import RemovePopup from "../../../components/common/popup/remove-popup";
import ConfirmPopup from "../../../components/common/popup/confirm-popup";
import DocumentBodyImport from "./document-body-import";
import AddProduct from "../../../routes/catalog/product/add-product";
import ReportViewer from "../../../components/partials/report/report-viewer";
import FormPopup from "../../../components/common/popup/form-popup";

import DocumentBodyDimensionsCell from "./document-body-dimensions-cell";
import DocumentProductEditPanel from "./document-product-edit-panel";
import DocumentBodyToolbar from "./document-body-toolbar";
import DocumentBodyExportOptionsModal from "./document-body-export-options-modal";
import DocumentBodyProductLookupCell from "./document-body-product-lookup-cell";
import DocumentBodyProductAdvancedSearch from "./document-body-product-advanced-search";
import DocumentBodyCatalogPanel from "./document-body-catalog-panel";
import DocumentBodyBulkPurchasePriceModal from "./document-body-bulk-purchase-price-modal";
import {getProduct} from "../../../api/catalog";
import {postElement} from "../../../api/config";
import DocumentBodyUnitPriceCell from "./document-body-unit-price-cell";
import DocumentBodyNetPriceCell from "./document-body-net-price-cell";
import DocumentBodyPurchasePriceCell from "./document-body-purchase-price-cell";
import DocumentBodyPurchasePriceHistoryPopup from "./document-body-purchase-price-history-popup";
import DocumentBodyEditableDateInput from "./document-body-editable-date-input";
import DocumentBodyEditableFormattedNumberInput from "./document-body-editable-number-input";
import DocumentBodyEditableDiscountListInput from "./document-body-editable-discount-input";
import DocumentBodyEditableTextarea from "./document-body-editable-textarea";
import DocumentBodyTotalCell from "./document-body-total-cell";
import DocumentBodyConfirmedQuantityModal from "./document-body-confirmed-quantity-modal";
import DocumentBodyReorderModal from "./document-body-reorder-modal";
import {hasPermissionFor} from "../../../components/partials/restricted/base-permission-wrapper";
import {permissionGroup, permissionSpecific, permissionType} from "../../../constants/permissions";
import {CONFIRMED} from "../../../constants/config";
import {
    confirmDocumentBodyRows,
    cloneDocumentBodyRows,
    createDocumentBodyRow,
    deleteDocumentBodyRows,
    exportDocument,
    fetchDocumentBody,
    hideDocumentBodyRows,
    insertDocumentBodyRows,
    updateDocumentBodyPositions,
    updateDocumentBodyPurchasePrices,
    updateDocumentBodyRow,
    unhideDocumentBodyRows,
} from "../services/document-service";

import {
    DOCUMENT_BODY_IMPORT_CONFIG,
    DOCUMENT_BODY_IMPORT_TYPE,
    NUMERIC_FIELDS,
    applyDiscountsToValue,
    calculateTotalFromNetPrice,
    deriveNetPrice,
    deriveRowsFromDocument,
    normalizeBodyRows,
    normalizeDiscountList,
    normalizeRowProduct,
    prepareApiValue,
    resolveDocumentCurrencySymbol,
    resolveDocumentType,
} from "./document-body-utils";
import {getPathParam} from "../../../utils/converter";
import {PRODUCT, PRICE_HISTORY} from "../../../api/endpoints";
import {AddFormPriceHistory} from "../../../routes/catalog/product/edit/pricing/price-history";
import {BULK_PURCHASE_PRICE_SCOPE} from "./document-body-bulk-actions-constants";
import {
    DOCUMENT_BODY_OVERLAY_Z_INDEX_CLASS,
    DOCUMENT_REPORT_VIEWER_Z_INDEX_CLASS,
    DOCUMENT_TABLE_TOOLBAR_Z_INDEX,
    resolveDocumentPermissionGroup,
} from "../constants";
import {
    resolveRequestEventBodyId,
    resolveRequestEventProductIdentifiers,
} from "../utils/request-event-utils";

const CONFIRMED_QTY_EVENT_TYPE = "CONFIRMED_QTY";
const MAX_SCROLL_ATTEMPTS = 12;
const SCROLLABLE_OVERFLOW_VALUES = new Set(["auto", "scroll", "overlay"]);
const EMPTY_PERMISSIONS = Object.freeze({
    isSuperAdmin: false,
    isAdmin: false,
    permissionDict: Object.freeze({}),
    permissionSpecificDict: Object.freeze({}),
});
// Matches Tailwind's mobile breakpoint to disable pinned columns on smaller screens.
const MOBILE_PINNING_MEDIA_QUERY = "(max-width: 767px)";
const ROW_VIRTUALIZATION_MIN_COUNT = 40;
const VIRTUAL_ROW_ESTIMATE_SIZE = 50;
const VIRTUAL_ROW_OVERSCAN = 4;

const normalizeStatusValue = (status) => {
    if (typeof status !== "string") {
        return "";
    }
    return status.trim().toLowerCase();
};

const NORMALIZED_CONFIRMED_STATUS = normalizeStatusValue(CONFIRMED);

// Walk up the DOM tree to locate the scrollable container enclosing the table rows.
const findScrollableParent = (element, boundary) => {
    if (typeof window === "undefined") {
        return null;
    }
    const isScrollable = (node) => {
        if (!node || !(node instanceof HTMLElement)) {
            return false;
        }
        const styles = window.getComputedStyle(node);
        if (!styles) {
            return false;
        }
        const overflowY = styles.overflowY ?? "";
        const overflow = styles.overflow ?? "";
        const hasScrollableOverflow = SCROLLABLE_OVERFLOW_VALUES.has(overflowY) || SCROLLABLE_OVERFLOW_VALUES.has(overflow);
        if (!hasScrollableOverflow) {
            return false;
        }
        return node.scrollHeight > node.clientHeight + 1;
    };

    let current = element?.parentElement ?? null;
    while (current && current instanceof HTMLElement) {
        if (isScrollable(current)) {
            return current;
        }
        if (boundary && current === boundary) {
            break;
        }
        current = current.parentElement;
    }

    if (boundary && isScrollable(boundary)) {
        return boundary;
    }

    return null;
};

const mergeServerRowPayload = (row, payload) => {
    if (!row || !payload || typeof payload !== "object") {
        return row;
    }
    const patch = payload.body && typeof payload.body === "object"
        ? payload.body
        : payload;
    if (!patch || typeof patch !== "object") {
        return row;
    }
    const nextRow = {...row};
    let nextProduct = row.product ? {...row.product} : null;
    const assignNumericField = (field) => {
        if (patch[field] === undefined) {
            return;
        }
        const numeric = Number(patch[field]);
        nextRow[field] = Number.isNaN(numeric) ? patch[field] : numeric;
        if (["purchase_price", "list_price", "unit_price", "net_price"].includes(field)) {
            if (!nextProduct && row.product) {
                nextProduct = {...row.product};
            }
            if (nextProduct) {
                nextProduct[field] = nextRow[field];
            }
        }
    };

    assignNumericField("purchase_price");
    assignNumericField("list_price");
    assignNumericField("unit_price");
    assignNumericField("net_price");
    assignNumericField("total_net_price");
    assignNumericField("discount_price");
    assignNumericField("total");

    if (patch.list_price_discounts !== undefined) {
        const normalizedListDiscounts = normalizeDiscountList(patch.list_price_discounts);
        nextRow.list_price_discounts = normalizedListDiscounts;
        nextRow.list_price_discount_string = Array.isArray(normalizedListDiscounts) && normalizedListDiscounts.length > 0
            ? normalizedListDiscounts.join("|")
            : "";
        if (!nextProduct && row.product) {
            nextProduct = {...row.product};
        }
        if (nextProduct) {
            nextProduct.list_price_discounts = normalizedListDiscounts;
        }
    }

    if (patch.unit_price_discounts !== undefined || patch.discount_list !== undefined) {
        const unitDiscountSource = patch.unit_price_discounts ?? patch.discount_list;
        const normalizedUnitDiscounts = normalizeDiscountList(unitDiscountSource);
        nextRow.unit_price_discounts = normalizedUnitDiscounts;
        nextRow.discount_list = normalizedUnitDiscounts;
        nextRow.unit_price_discount_string = Array.isArray(normalizedUnitDiscounts) && normalizedUnitDiscounts.length > 0
            ? normalizedUnitDiscounts.join("|")
            : "";
        nextRow.discount_string = nextRow.unit_price_discount_string;
        if (!nextProduct && row.product) {
            nextProduct = {...row.product};
        }
        if (nextProduct) {
            nextProduct.unit_price_discounts = normalizedUnitDiscounts;
            nextProduct.discount = normalizedUnitDiscounts;
        }
    }

    if (patch.delivery_date !== undefined) {
        nextRow.delivery_date = patch.delivery_date;
    }

    ["discount_string", "list_price_discount_string", "unit_price_discount_string"].forEach((field) => {
        if (patch[field] !== undefined) {
            nextRow[field] = patch[field];
        }
    });

    if (patch.product && typeof patch.product === "object") {
        if (!nextProduct && row.product) {
            nextProduct = {...row.product};
        }
        nextProduct = {...(nextProduct ?? {}), ...patch.product};
    }

    if (nextProduct) {
        nextRow.product = nextProduct;
    }

    return nextRow;
};

const escapeRowSelector = (value) => {
    if (value === null || value === undefined) {
        return "";
    }
    const stringValue = String(value);
    if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
        return CSS.escape(stringValue);
    }
    return stringValue.replace(/([ !"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, "\\$1");
};

const numericOrNull = (value) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};


const sumConfirmedQuantities = (entries) => {
    if (!Array.isArray(entries) || entries.length === 0) {
        return 0;
    }
    return entries.reduce((total, entry) => {
        const quantityValue = numericOrNull(
            entry?.quantity
            ?? entry?.confirmed_quantity
            ?? entry?.qty
            ?? entry?.value
        );
        return total + (quantityValue ?? 0);
    }, 0);
};

const extractManagedEventsList = (managedEvents) => {
    if (!managedEvents) {
        return [];
    }
    if (Array.isArray(managedEvents)) {
        return managedEvents;
    }
    if (typeof managedEvents === "object") {
        if (Array.isArray(managedEvents.events)) {
            return managedEvents.events;
        }
        if (Array.isArray(managedEvents.data)) {
            return managedEvents.data;
        }
        if (Array.isArray(managedEvents.items)) {
            return managedEvents.items;
        }
        if (Array.isArray(managedEvents.rows)) {
            return managedEvents.rows;
        }
    }
    return [];
};

const resolveQuantityFromManagedEvents = (managedEvents) => {
    if (!managedEvents) {
        return 0;
    }
    if (Array.isArray(managedEvents)) {
        return sumConfirmedQuantities(managedEvents);
    }
    if (typeof managedEvents === "object") {
        const quantityCandidate = numericOrNull(
            managedEvents.quantity
            ?? managedEvents.total_quantity
            ?? managedEvents.totalQuantity
            ?? managedEvents.quantity_total
            ?? managedEvents.quantityTotal
            ?? managedEvents.confirmed_quantity
        );
        if (quantityCandidate !== null) {
            return quantityCandidate;
        }
        const eventsList = extractManagedEventsList(managedEvents);
        return sumConfirmedQuantities(eventsList);
    }
    return 0;
};

const resolveConfirmedQuantityFromRow = (row) => {
    if (!row || typeof row !== "object") {
        return 0;
    }
    const candidates = [
        numericOrNull(row.confirmed_quantity_total),
        numericOrNull(row.confirmedQuantityTotal),
        numericOrNull(row.confirmed_quantity_sum),
        numericOrNull(row.confirmedQuantitySum),
        numericOrNull(row.total_confirmed_quantity),
        numericOrNull(row.totalConfirmedQuantity),
        numericOrNull(row.total_confirmed_qty),
        numericOrNull(row.totalConfirmedQty),
        numericOrNull(row.confirmed_qty_total),
        numericOrNull(row.confirmedQtyTotal),
        numericOrNull(row.confirmed_qty),
        numericOrNull(row.confirmedQty),
        numericOrNull(row.confirmed_quantity),
        numericOrNull(row.confirmedQuantity),
    ];
    const candidateMatch = candidates.find((value) => value !== null);
    if (candidateMatch !== undefined && candidateMatch !== null) {
        return candidateMatch;
    }
    if (Array.isArray(row.confirmed_quantity_events)) {
        const quantity = sumConfirmedQuantities(row.confirmed_quantity_events);
        if (quantity > 0) {
            return quantity;
        }
    }
    if (Array.isArray(row.document_events)) {
        const quantity = sumConfirmedQuantities(
            row.document_events.filter((entry) => {
                const rawType = entry?.type ?? entry?.event_type ?? entry?.eventType ?? "";
                const normalizedType = typeof rawType === "string" ? rawType.toUpperCase() : "";
                return normalizedType === CONFIRMED_QTY_EVENT_TYPE;
            })
        );
        if (quantity > 0) {
            return quantity;
        }
    }
    return 0;
};

const normalizeComparableString = (value) => {
    if (value === null || value === undefined) {
        return "";
    }
    return String(value).trim().toLowerCase();
};

const doesRowMatchId = (row, targetId) => {
    const normalizedTarget = normalizeComparableString(targetId);
    if (!normalizedTarget) {
        return false;
    }
    const rowCandidates = [
        row?.id,
        row?.body_id,
        row?.bodyId,
        row?.document_body_id,
        row?.documentBodyId,
        row?.document_body?.id,
        row?.documentBody?.id,
        row?.body?.id,
    ];
    return rowCandidates.some((candidate) => (
        candidate !== null
        && candidate !== undefined
        && normalizeComparableString(candidate) === normalizedTarget
    ));
};

const findRowByBodyId = (rows, bodyId) => {
    const normalizedBodyId = normalizeComparableString(bodyId);
    if (!normalizedBodyId || !Array.isArray(rows)) {
        return null;
    }
    return rows.find((row) => doesRowMatchId(row, normalizedBodyId)) ?? null;
};

const resolveRowIdentifier = (row) => {
    if (!row || typeof row !== "object") {
        return null;
    }
    const candidates = [
        row.id,
        row.body_id,
        row.bodyId,
        row.document_body_id,
        row.documentBodyId,
        row.document_body?.id,
        row.documentBody?.id,
        row.body?.id,
    ];
    const match = candidates.find((value) => value !== null && value !== undefined);
    return match !== undefined ? match : null;
};

const findRowByProductIdentifiers = (rows, identifiers) => {
    if (!identifiers || !Array.isArray(rows) || rows.length === 0) {
        return null;
    }
    const normalizedProductId = identifiers.productId ? normalizeComparableString(identifiers.productId) : "";
    if (normalizedProductId) {
        const matchById = rows.find((row) => {
            const productCandidates = [
                row?.product_id,
                row?.productId,
                row?.product?.id,
                row?.product?.product_id,
            ];
            return productCandidates.some((candidate) => (
                candidate !== null
                && candidate !== undefined
                && normalizeComparableString(candidate) === normalizedProductId
            ));
        });
        if (matchById) {
            return matchById;
        }
    }
    const normalizedProductCode = identifiers.productCode ? normalizeComparableString(identifiers.productCode) : "";
    if (normalizedProductCode) {
        const matchByCode = rows.find((row) => {
            const codeCandidates = [
                row?.product_reference,
                row?.productReference,
                row?.manufacture_reference,
                row?.product?.code,
                row?.product?.product_reference,
                row?.product?.productReference,
                row?.product?.manufacture_reference,
            ];
            return codeCandidates.some((candidate) => (
                candidate
                && normalizeComparableString(candidate) === normalizedProductCode
            ));
        });
        if (matchByCode) {
            return matchByCode;
        }
    }
    return null;
};

const findRowForRequestEvent = (rows, eventItem) => {
    if (!eventItem || !Array.isArray(rows) || rows.length === 0) {
        return null;
    }
    const bodyId = resolveRequestEventBodyId(eventItem);
    if (bodyId) {
        const rowById = findRowByBodyId(rows, bodyId);
        if (rowById) {
            return rowById;
        }
    }
    const identifiers = resolveRequestEventProductIdentifiers(eventItem);
    if (identifiers) {
        const rowByProduct = findRowByProductIdentifiers(rows, identifiers);
        if (rowByProduct) {
            return rowByProduct;
        }
    }
    return null;
};

const deriveManagedConfirmedQuantityState = (row) => {
    if (!row || typeof row !== "object") {
        return null;
    }

    const eventsSource = row.confirmed_quantity_events ?? row.confirmedQuantityEvents;
    const eventsList = extractManagedEventsList(eventsSource);
    const quantityFromRow = resolveConfirmedQuantityFromRow(row);
    const derivedQuantity = quantityFromRow > 0
        ? quantityFromRow
        : sumConfirmedQuantities(eventsList);
    const totalCandidate = numericOrNull(
        row.confirmed_quantity_events_count
        ?? row.confirmedQuantityEventsCount
        ?? row.confirmed_quantity_count
        ?? row.confirmedQuantityCount
    );
    const total = totalCandidate !== null
        ? Number(totalCandidate)
        : eventsList.length;

    const hasQuantity = derivedQuantity > 0;
    const hasEvents = eventsList.length > 0;
    const hasTotal = total > 0;

    if (!hasQuantity && !hasEvents && !hasTotal) {
        return null;
    }

    return {
        events: eventsList,
        total,
        quantity: derivedQuantity,
    };
};

const managedEventsShallowEqual = (first, second) => {
    if (first === second) {
        return true;
    }
    if (!first || !second) {
        return false;
    }
    const firstQuantity = resolveQuantityFromManagedEvents(first);
    const secondQuantity = resolveQuantityFromManagedEvents(second);
    if (firstQuantity !== secondQuantity) {
        return false;
    }
    const firstTotal = numericOrNull(first.total);
    const secondTotal = numericOrNull(second.total);
    if ((firstTotal ?? 0) !== (secondTotal ?? 0)) {
        return false;
    }
    const firstEvents = extractManagedEventsList(first);
    const secondEvents = extractManagedEventsList(second);
    return firstEvents.length === secondEvents.length;
};

const hasManagedEventsData = (managedEvents) => {
    if (!managedEvents) {
        return false;
    }
    if (resolveQuantityFromManagedEvents(managedEvents) > 0) {
        return true;
    }
    const totalValue = numericOrNull(managedEvents.total);
    if (totalValue !== null && totalValue > 0) {
        return true;
    }
    return extractManagedEventsList(managedEvents).length > 0;
};

const formatConfirmedQuantityBadge = (value, referenceQuantity) => {
    const confirmed = numericOrNull(value);
    const reference = numericOrNull(referenceQuantity);
    if (confirmed === null || confirmed <= 0) {
        return "";
    }
    if (reference === null || reference <= 0) {
        return `${Math.round(confirmed)}%`;
    }
    const percentage = (confirmed / reference) * 100;
    if (!Number.isFinite(percentage) || percentage <= 0) {
        return "";
    }
    return `${Math.round(percentage)}%`;
};

const normalizeDocumentIdentifier = (value) => {
    if (value === null || value === undefined) {
        return null;
    }
    return String(value);
};

const resolveRowPackagingQuantity = (row) => {
    if (!row) {
        return 0;
    }
    const candidates = [
        row.qty_per_packaging,
        row.product?.qty_per_packaging,
        row.product?.default_dimension?.qty_per_packaging,
    ];
    for (const candidate of candidates) {
        const numericValue = numericOrNull(candidate);
        if (numericValue && numericValue > 0) {
            return numericValue;
        }
    }
    return 0;
};

const QUANTITY_ALIGNMENT_TOLERANCE = 1e-9;

const areQuantitiesAligned = (candidate, reference) => {
    if (!Number.isFinite(candidate) || !Number.isFinite(reference)) {
        return false;
    }
    const tolerance = Math.max(1, Math.abs(candidate), Math.abs(reference)) * QUANTITY_ALIGNMENT_TOLERANCE;
    return Math.abs(candidate - reference) <= tolerance;
};

const formatSuggestedQuantity = (value) => {
    if (!Number.isFinite(value)) {
        return "";
    }
    const formatted = value.toFixed(6).replace(/\.?0+$/, "");
    return formatted === "" ? "0" : formatted;
};

const resolveAlignedPackagingQuantity = (quantity, packagingSize) => {
    const packagingQuantity = numericOrNull(packagingSize);
    const desiredQuantity = numericOrNull(quantity);

    if (!packagingQuantity || packagingQuantity <= 0 || desiredQuantity === null) {
        return {packagingQuantity, desiredQuantity, alignedQuantity: null};
    }

    if (desiredQuantity <= 0) {
        return {
            packagingQuantity,
            desiredQuantity,
            alignedQuantity: packagingQuantity,
        };
    }

    const suggestedQuantityPerPackage = Math.ceil(desiredQuantity / packagingQuantity);
    return {
        packagingQuantity,
        desiredQuantity,
        alignedQuantity: packagingQuantity * suggestedQuantityPerPackage,
    };
};

const hasPackagingQuantityMismatch = (quantity, packagingSize) => {
    const {
        packagingQuantity,
        desiredQuantity,
        alignedQuantity,
    } = resolveAlignedPackagingQuantity(quantity, packagingSize);

    if (!packagingQuantity || packagingQuantity <= 0 || desiredQuantity === null) {
        return false;
    }
    if (desiredQuantity <= 0) {
        return false;
    }
    if (areQuantitiesAligned(desiredQuantity, 1)) {
        return false;
    }
    if (alignedQuantity === null) {
        return false;
    }
    return !areQuantitiesAligned(alignedQuantity, desiredQuantity);
};

const resolvePackagingSuggestionMessage = (quantity, packagingSize, t) => {
    const {
        packagingQuantity,
        desiredQuantity,
        alignedQuantity,
    } = resolveAlignedPackagingQuantity(quantity, packagingSize);

    if (!packagingQuantity || packagingQuantity <= 0 || desiredQuantity === null || alignedQuantity === null) {
        return "";
    }

    const needsSuggestion = desiredQuantity <= 0 || !areQuantitiesAligned(alignedQuantity, desiredQuantity);
    if (!needsSuggestion) {
        return "";
    }

    const prefix = t("app.container.product_predict", {defaultValue: "Quantità suggerita: "});
    return `${prefix}${formatSuggestedQuantity(alignedQuantity)}`;
};

const resolvePackagingWarningDetails = (quantity, packagingSize, t) => {
    const suggestionMessage = resolvePackagingSuggestionMessage(quantity, packagingSize, t);
    if (!suggestionMessage) {
        return null;
    }
    const invalidPackaging = t("app.container.packaging_invalid", {defaultValue: "Imballaggio non valido"});
    return {
        title: invalidPackaging,
        description: suggestionMessage,
    };
};

const DocumentBody = ({
    document,
    companyId,
    documentId,
    documentType,
    isLoading,
    importRefreshKey,
    onImportQueued,
    showMargin,
    onDocumentTotalsRefresh,
    canEdit,
    stickyToolbarOffset,
    enableStickyToolbar,
    externalConfirmedQuantityRequest,
    onConfirmedQuantityEventsChange,
}) => {
    const {t} = useTranslation();
    const {isExporter} = useSelector((state) => state.user ?? {});
    const {company, permissions: companyPermissions} = useSelector((state) => state.userCompany ?? {});
    const resolvedPermissions = companyPermissions ?? EMPTY_PERMISSIONS;
    const documentPermissionGroup = useMemo(
        () => resolveDocumentPermissionGroup({type: documentType}) ?? permissionGroup.DOCUMENT,
        [documentType],
    );

    const canIncludeSensitiveDocumentData = hasPermissionFor(resolvedPermissions, documentPermissionGroup, permissionSpecific.DOCUMENT_DOCUMENT_SENS_DATA);



    const canCloneDocument = hasPermissionFor(resolvedPermissions, documentPermissionGroup, permissionSpecific.DOCUMENT_CLONE);
    const canHideDocumentRows = hasPermissionFor(resolvedPermissions, documentPermissionGroup, permissionSpecific.DOCUMENT_HIDE_PRODUCT);
    const canViewSellingHistory = hasPermissionFor(resolvedPermissions, documentPermissionGroup, permissionSpecific.DOCUMENT_SELLING_HISTORY);
    const canViewPurchaseHistory = hasPermissionFor(resolvedPermissions, documentPermissionGroup, permissionSpecific.DOCUMENT_PURCHASE_HISTORY);
    const canCreateDocumentBody = hasPermissionFor(resolvedPermissions, documentPermissionGroup, permissionSpecific.DOCUMENT_CREATE_BODY);
    const canImportDocumentProduct = hasPermissionFor(resolvedPermissions, documentPermissionGroup, permissionSpecific.DOCUMENT_IMPORT_PRODUCT);
    const canCreateCatalogProductPermission = hasPermissionFor(resolvedPermissions, documentPermissionGroup, permissionSpecific.DOCUMENT_CREATE_PRODUCT_ON_CATALOG);
    const canViewCatalogProducts = hasPermissionFor(resolvedPermissions, documentPermissionGroup, permissionSpecific.DOCUMENT_VIEW_PRODUCT_CATALOG);
    const canManageDocumentConfirmedQuantity = hasPermissionFor(resolvedPermissions, documentPermissionGroup, permissionSpecific.DOCUMENT_MANAGE_EVENT_CONFIRMATION);
    const canViewMarginData = hasPermissionFor(resolvedPermissions, documentPermissionGroup, permissionSpecific.DOCUMENT_MARGIN_DATA);
    const canExportDocument = hasPermissionFor(resolvedPermissions, documentPermissionGroup, permissionSpecific.DOCUMENT_EXPORT);
    const storeCurrencySymbol = company?.currency?.currency_symbol ?? "";
    const canCreateProductBase = hasPermissionFor(resolvedPermissions, permissionGroup.PRODUCT, permissionType.CREATE);
    const canEditProduct = hasPermissionFor(resolvedPermissions, permissionGroup.PRODUCT, permissionType.EDIT);
    const canCreateProductRate = hasPermissionFor(resolvedPermissions, permissionGroup.PRODUCT_RATE, permissionType.CREATE);
    const canCreateProduct = canCreateCatalogProductPermission && canCreateProductBase;
    const canAccessCatalog = canViewCatalogProducts;
    const isEditable = Boolean(canEdit);
    const isDocumentConfirmed = normalizeStatusValue(document?.status) === NORMALIZED_CONFIRMED_STATUS;
    const canAddDocumentRows = canCreateDocumentBody && isEditable;
    const [rows, setRows] = useState(() => deriveRowsFromDocument(
        document,
        resolveDocumentCurrencySymbol(document, storeCurrencySymbol)
    ));
    const totalRowCount = rows.length;
    const shouldVirtualizeRows = totalRowCount >= ROW_VIRTUALIZATION_MIN_COUNT;
    const [selectedRows, setSelectedRows] = useState({});
    const [isBodyLoading, setBodyLoading] = useState(false);
    const [isVisibilityUpdating, setIsVisibilityUpdating] = useState(false);
    const [bodyError, setBodyError] = useState(null);
    const [updateError, setUpdateError] = useState(null);
    const [rowPendingRemove, setRowPendingRemove] = useState(null);
    const [isRemoveOpen, setIsRemoveOpen] = useState(false);
    const [isCreatingRow, setIsCreatingRow] = useState(false);
    const [isBulkPurchasePriceModalOpen, setIsBulkPurchasePriceModalOpen] = useState(false);
    const [bulkPurchasePriceScope, setBulkPurchasePriceScope] = useState(BULK_PURCHASE_PRICE_SCOPE.FILL_MISSING);
    const [isBulkPurchasePriceUpdating, setIsBulkPurchasePriceUpdating] = useState(false);
    const [isBulkConfirmQuantityUpdating, setIsBulkConfirmQuantityUpdating] = useState(false);
    const [isBulkConfirmQuantityConfirmOpen, setIsBulkConfirmQuantityConfirmOpen] = useState(false);
    const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);
    const [isReorderSubmitting, setIsReorderSubmitting] = useState(false);
    const [isAddRateOpen, setIsAddRateOpen] = useState(false);
    const [addRateProduct, setAddRateProduct] = useState(null);
    const [addRateDefaults, setAddRateDefaults] = useState(null);
    const [isAddRateSubmitting, setIsAddRateSubmitting] = useState(false);
    const [addRateError, setAddRateError] = useState(null);
    const [isPurchaseHistoryOpen, setIsPurchaseHistoryOpen] = useState(false);
    const [purchaseHistoryRowId, setPurchaseHistoryRowId] = useState(null);
    const purchaseHistoryRow = useMemo(() => (
        purchaseHistoryRowId
            ? rows.find((row) => row.id === purchaseHistoryRowId) ?? null
            : null
    ), [purchaseHistoryRowId, rows]);

    useEffect(() => {
        if (isPurchaseHistoryOpen && purchaseHistoryRowId && !purchaseHistoryRow) {
            setIsPurchaseHistoryOpen(false);
            setPurchaseHistoryRowId(null);
        }
    }, [isPurchaseHistoryOpen, purchaseHistoryRow, purchaseHistoryRowId]);
    const documentCurrencySymbol = useMemo(
        () => resolveDocumentCurrencySymbol(document, storeCurrencySymbol),
        [document, storeCurrencySymbol]
    );
    const documentCurrencyCode = useMemo(
        () => document?.currency?.code
            ?? document?.currency_code
            ?? document?.company?.currency?.code
            ?? null,
        [document]
    );
    const resolvedShowMargin = showMargin && canViewMarginData;
    const previousCurrencyRef = useRef({
        code: documentCurrencyCode,
        symbol: documentCurrencySymbol,
    });
    const documentDeliveryDateRawRef = useRef(document?.delivery_date ?? "");
    const documentDeliveryDateRef = useRef("");
    useEffect(() => {
        const rawDeliveryDate = document?.delivery_date ?? "";
        documentDeliveryDateRawRef.current = rawDeliveryDate;
        const preparedDeliveryDate = prepareApiValue("delivery_date", rawDeliveryDate);
        if (typeof preparedDeliveryDate === "string" && preparedDeliveryDate.length >= 10) {
            documentDeliveryDateRef.current = preparedDeliveryDate.slice(0, 10);
            return;
        }
        documentDeliveryDateRef.current = rawDeliveryDate ? String(rawDeliveryDate) : "";
    }, [document?.delivery_date]);
    const shouldScrollRef = useRef(false);
    const scrollAttemptRef = useRef(0);
    const pendingScrollRowIdRef = useRef(null);
    const deleteResponseRef = useRef(null);
    const tableWrapperRef = useRef(null);
    const [isAddProductOpen, setIsAddProductOpen] = useState(false);
    const [isEditProductOpen, setIsEditProductOpen] = useState(false);
    const [editProductId, setEditProductId] = useState(null);
    const [editProductRowId, setEditProductRowId] = useState(null);
    const [addProductRowId, setAddProductRowId] = useState(null);
    const [isCatalogPanelOpen, setIsCatalogPanelOpen] = useState(false);
    const [isCatalogSubmitting, setIsCatalogSubmitting] = useState(false);
    const [isProductAdvancedSearchOpen, setIsProductAdvancedSearchOpen] = useState(false);
    const [advancedSearchRowId, setAdvancedSearchRowId] = useState(null);
    const [isBatchConfirmOpen, setIsBatchConfirmOpen] = useState(false);
    const [batchAction, setBatchAction] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const searchRequestControllerRef = useRef(null);
    const searchTermRef = useRef(searchTerm);
    const [searchResetToken, setSearchResetToken] = useState(null);
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [isExportOpen, setIsExportOpen] = useState(false);
    const [includeSensitiveData, setIncludeSensitiveData] = useState(false);
    const [includeProductDataImages, setIncludeProductDataImages] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [exportError, setExportError] = useState(null);
    const [isExportReportOpen, setIsExportReportOpen] = useState(false);
    const [exportReportUrl, setExportReportUrl] = useState("");
    const [isConfirmedQuantityOpen, setIsConfirmedQuantityOpen] = useState(false);
    const [confirmedQuantityRowId, setConfirmedQuantityRowId] = useState(null);
    const [confirmedQuantityRowMax, setConfirmedQuantityRowMax] = useState(null);
    const [confirmedQuantityRowExistingTotal, setConfirmedQuantityRowExistingTotal] = useState(null);
    const [confirmedQuantityEvents, setConfirmedQuantityEvents] = useState({});
    const externalRequestProcessedRef = useRef(null);
    const [isMobileViewport, setIsMobileViewport] = useState(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
            return false;
        }
        return window.matchMedia(MOBILE_PINNING_MEDIA_QUERY).matches;
    });
    useEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
            return undefined;
        }
        const mediaQuery = window.matchMedia(MOBILE_PINNING_MEDIA_QUERY);
        const handleChange = (event) => {
            setIsMobileViewport(event.matches);
        };
        setIsMobileViewport(mediaQuery.matches);
        if (typeof mediaQuery.addEventListener === "function") {
            mediaQuery.addEventListener("change", handleChange);
            return () => mediaQuery.removeEventListener("change", handleChange);
        }
        if (typeof mediaQuery.addListener === "function") {
            mediaQuery.addListener(handleChange);
            return () => mediaQuery.removeListener(handleChange);
        }
        return undefined;
    }, []);
    useEffect(() => {
        if (isEditable) {
            return;
        }
        setIsAddProductOpen(false);
        setIsEditProductOpen(false);
        setIsProductAdvancedSearchOpen(false);
        setIsCatalogPanelOpen(false);
        setIsCatalogSubmitting(false);
        setIsBatchConfirmOpen(false);
        setIsImportOpen(false);
        setIsExportOpen(false);
        setIsExportReportOpen(false);
        setExportReportUrl("");
        setIsConfirmedQuantityOpen(false);
        setIsBulkConfirmQuantityConfirmOpen(false);
        setIsBulkConfirmQuantityUpdating(false);
        setBatchAction(null);
        setAddProductRowId(null);
        setEditProductRowId(null);
        setEditProductId(null);
        setAdvancedSearchRowId(null);
        setConfirmedQuantityRowId(null);
        setConfirmedQuantityRowMax(null);
        setConfirmedQuantityRowExistingTotal(null);
        setIsAddRateOpen(false);
        setAddRateProduct(null);
        setAddRateDefaults(null);
        setAddRateError(null);
        setIsPurchaseHistoryOpen(false);
        setPurchaseHistoryRowId(null);
    }, [isEditable]);

    const ensureEditable = useCallback(() => {
        if (isEditable) {
            return true;
        }
        toast.error(t("app.common.permission_denied", {defaultValue: "Operazione non consentita"}));
        return false;
    }, [isEditable, t]);
    const ensureExportReady = useCallback(() => {
        if (isEditable || isDocumentConfirmed) {
            return true;
        }
        return ensureEditable();
    }, [ensureEditable, isDocumentConfirmed, isEditable]);
    const handleOpenReorderModal = useCallback(() => {
        if (!ensureEditable()) {
            return;
        }
        setIsReorderModalOpen(true);
    }, [ensureEditable]);
    const handleCloseReorderModal = useCallback(() => {
        if (isReorderSubmitting) {
            return;
        }
        setIsReorderModalOpen(false);
    }, [isReorderSubmitting]);
    const documentRef = useRef(document);
    const documentCurrencySymbolRef = useRef(documentCurrencySymbol);
    const documentSnapshotRef = useRef({
        id: normalizeDocumentIdentifier(document?.id ?? documentId ?? null),
        hasDocument: Boolean(document),
    });
    const lastBodyFetchDocumentIdRef = useRef(normalizeDocumentIdentifier(document?.id ?? documentId ?? null));
    const importRefreshKeyRef = useRef(importRefreshKey);
    const notifyDocumentTotalsChange = useCallback(() => {
        if (typeof onDocumentTotalsRefresh === "function") {
            onDocumentTotalsRefresh();
        }
    }, [onDocumentTotalsRefresh]);
    const assignedProductIds = useMemo(() => (
        rows
            .map((row) => row?.product?.id)
            .filter((id) => id !== null && id !== undefined)
    ), [rows]);
    const resolvedCompanyId = useMemo(() => (
        companyId
        ?? document?.company?.id
        ?? document?.company_id
        ?? document?.companyId
        ?? document?.header?.company_id
        ?? company?.id
        ?? null
    ), [companyId, company, document]);
    const resolvedDocumentType = useMemo(
        () => resolveDocumentType(document, documentType),
        [document, documentType]
    );
    const bodyFetchParams = useMemo(() => ({limit: 1000, offset: 0}), []);

    const handleSearch = useCallback((query) => {
        const term = (query ?? "").trim();
        setSearchTerm(term);

        if (!resolvedCompanyId || !documentId) {
            return;
        }

        if (searchRequestControllerRef.current) {
            searchRequestControllerRef.current.abort();
        }

        const nextController = new AbortController();
        searchRequestControllerRef.current = nextController;

        const params = term
            ? {...bodyFetchParams, query: term}
            : {...bodyFetchParams};

        setBodyError(null);
        setBodyLoading(true);

        fetchDocumentBody({
            companyId: resolvedCompanyId,
            documentId,
            params,
            signal: nextController.signal,
        })
            .then((payload) => {
                const currentDocument = documentRef.current;
                const currentCurrencySymbol = documentCurrencySymbolRef.current;
                const normalized = normalizeBodyRows(payload, currentDocument, currentCurrencySymbol);

                const baseRows = normalized.length > 0
                    ? normalized
                    : deriveRowsFromDocument(currentDocument, currentCurrencySymbol);

                if (!term) {
                    setRows(baseRows);
                    lastBodyFetchDocumentIdRef.current = normalizeDocumentIdentifier(
                        documentId ?? (currentDocument?.id ?? null)
                    );
                    return;
                }

                setRows(baseRows);
            })
            .catch((error) => {
                if (error?.name === "AbortError") {
                    return;
                }
                setBodyError(error);
            })
            .finally(() => {
                if (searchRequestControllerRef.current === nextController) {
                    searchRequestControllerRef.current = null;
                    setBodyLoading(false);
                }
            });
    }, [bodyFetchParams, documentId, resolvedCompanyId]);

    const handleOpenImport = useCallback(() => {
        if (!canImportDocumentProduct) {
            toast.error(t("app.common.permission_denied", {defaultValue: "Operazione non consentita"}));
            return;
        }
        if (!ensureEditable()) {
            return;
        }
        setIsImportOpen(true);
    }, [canImportDocumentProduct, ensureEditable, t]);

    const handleCloseImport = useCallback(() => {
        setIsImportOpen(false);
    }, []);

    const handleOpenExport = useCallback(() => {
        if (!canExportDocument) {
            toast.error(t("app.common.permission_denied", {defaultValue: "Operazione non consentita"}));
            return;
        }
        if (!ensureExportReady()) {
            return;
        }
        if (!resolvedCompanyId || !documentId || !resolvedDocumentType) {
            toast.error(t("app.documents.export_missing_params", {defaultValue: "Impossibile avviare l'esportazione"}));
            return;
        }

        setIncludeSensitiveData(false);
        setIncludeProductDataImages(false);
        setExportError(null);
        setIsExportOpen(true);
    }, [canExportDocument, documentId, ensureExportReady, resolvedCompanyId, resolvedDocumentType, t]);

    const handleCloseExport = useCallback(() => {
        if (isExporting) {
            return;
        }
        setIsExportOpen(false);
    }, [isExporting]);

    const handleToggleReportViewer = useCallback((nextOpen) => {
        setIsExportReportOpen(Boolean(nextOpen));
        if (!nextOpen) {
            setExportReportUrl("");
        }
    }, []);

    useEffect(() => {
        searchTermRef.current = searchTerm;
    }, [searchTerm]);

    useEffect(() => () => {
        if (searchRequestControllerRef.current) {
            searchRequestControllerRef.current.abort();
        }
    }, []);

    useEffect(() => {
        if (!canIncludeSensitiveDocumentData && includeSensitiveData) {
            setIncludeSensitiveData(false);
        }
    }, [canIncludeSensitiveDocumentData, includeSensitiveData]);

    useEffect(() => {
        if (!canImportDocumentProduct && isImportOpen) {
            setIsImportOpen(false);
        }
    }, [canImportDocumentProduct, isImportOpen]);

    useEffect(() => {
        if (!canExportDocument && isExportOpen) {
            setIsExportOpen(false);
        }
    }, [canExportDocument, isExportOpen]);

    useEffect(() => {
        if (!canCreateProduct && isAddProductOpen) {
            setIsAddProductOpen(false);
            setAddProductRowId(null);
        }
    }, [canCreateProduct, isAddProductOpen]);

    useEffect(() => {
        if (!canManageDocumentConfirmedQuantity && isConfirmedQuantityOpen) {
            setIsConfirmedQuantityOpen(false);
            setConfirmedQuantityRowId(null);
        }
    }, [canManageDocumentConfirmedQuantity, isConfirmedQuantityOpen]);

    useEffect(() => {
        if (!canAccessCatalog && isCatalogPanelOpen) {
            setIsCatalogPanelOpen(false);
        }
    }, [canAccessCatalog, isCatalogPanelOpen]);

    const handleConfirmExport = useCallback(async () => {
        if (!canExportDocument) {
            toast.error(t("app.common.permission_denied", {defaultValue: "Operazione non consentita"}));
            return;
        }
        if (!ensureExportReady()) {
            return;
        }
        if (!resolvedCompanyId || !documentId || !resolvedDocumentType) {
            toast.error(t("app.documents.export_missing_params", {defaultValue: "Impossibile avviare l'esportazione"}));
            return;
        }

        setIsExporting(true);
        setExportError(null);

        try {
            const response = await exportDocument({
                companyId: resolvedCompanyId,
                type: resolvedDocumentType,
                documentId,
                data: {
                    include_sensitive_data: includeSensitiveData,
                    include_product_data_images: includeProductDataImages,
                },
            });

            const downloadTargets = [
                response?.file?.reference,
                response?.file?.url,
                response?.url,
                response?.download_url,
            ].filter(Boolean);

            toast.success(t("app.documents.export_success", {defaultValue: "Esportazione avviata"}));
            setIsExportOpen(false);
            if (downloadTargets.length > 0) {
                setExportReportUrl(downloadTargets[0]);
                setIsExportReportOpen(true);
            }
        } catch (error) {
            const message = error?.message ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"});
            setExportError(message);
            toast.error(message);
        } finally {
            setIsExporting(false);
        }
    }, [canExportDocument, documentId, ensureExportReady, includeProductDataImages, includeSensitiveData, resolvedCompanyId, resolvedDocumentType, t]);

    const handleOpenConfirmedQuantity = useCallback((rowId) => {
        if (!canManageDocumentConfirmedQuantity) {
            toast.error(t("app.common.permission_denied", {defaultValue: "Operazione non consentita"}));
            return;
        }
        if (!ensureEditable()) {
            return;
        }
        if (!rowId) {
            return;
        }

        const rowKey = String(rowId);
        if (rowKey.startsWith("new-") || rowKey.startsWith("clone-")) {
            toast.error(t("app.documents.confirmed_quantity_unavailable_for_draft", {defaultValue: "Salva la riga prima di gestire le quantità confermate"}));
            return;
        }

        let resolvedRowQuantity = null;
        let resolvedExistingTotal = 0;
        const targetRow = rows.find((entry) => {
            const entryId = entry?.id;
            if (entryId === null || entryId === undefined) {
                return false;
            }
            return String(entryId) === rowKey;
        });

        if (targetRow) {
            resolvedRowQuantity = numericOrNull(
                targetRow.quantity
                ?? targetRow.qty
                ?? targetRow.total_quantity
                ?? targetRow.totalQuantity
            );
            const managedEvents = confirmedQuantityEvents[rowKey];
            let initialQuantity = resolveQuantityFromManagedEvents(managedEvents);
            if (initialQuantity === 0) {
                initialQuantity = resolveConfirmedQuantityFromRow(targetRow);
            }
            resolvedExistingTotal = initialQuantity ?? 0;
        }

        setConfirmedQuantityRowMax(resolvedRowQuantity);
        setConfirmedQuantityRowExistingTotal(resolvedExistingTotal);
        setConfirmedQuantityRowId(rowId);
        setIsConfirmedQuantityOpen(true);
        // eslint-disable-next-line
    }, [canManageDocumentConfirmedQuantity, confirmedQuantityEvents, ensureEditable, rows, t]);

    const handleCloseConfirmedQuantity = useCallback(() => {
        setIsConfirmedQuantityOpen(false);
        setConfirmedQuantityRowId(null);
        setConfirmedQuantityRowMax(null);
        setConfirmedQuantityRowExistingTotal(null);
    }, []);

    const handleConfirmedQuantityEventsChange = useCallback((rowId, eventsPayload) => {
        if (!ensureEditable()) {
            return;
        }
        if (!rowId) {
            return;
        }

        const key = String(rowId);
        let normalizedSummary = null;
        setConfirmedQuantityEvents((previous) => {
            const next = {...previous};
            if (!eventsPayload) {
                delete next[key];
                normalizedSummary = null;
            } else {
                let eventsList = [];
                let total = 0;
                let quantityTotal = 0;

                if (Array.isArray(eventsPayload)) {
                    eventsList = eventsPayload;
                    total = eventsPayload.length;
                    quantityTotal = sumConfirmedQuantities(eventsPayload);
                } else if (typeof eventsPayload === "object") {
                    if (Array.isArray(eventsPayload.events)) {
                        eventsList = eventsPayload.events;
                    } else if (Array.isArray(eventsPayload.data)) {
                        eventsList = eventsPayload.data;
                    } else if (Array.isArray(eventsPayload.items)) {
                        eventsList = eventsPayload.items;
                    } else if (Array.isArray(eventsPayload.rows)) {
                        eventsList = eventsPayload.rows;
                    }

                    const totalCandidate = numericOrNull(
                        eventsPayload.total
                        ?? eventsPayload.total_rows
                        ?? eventsPayload.totalRows
                    );
                    if (totalCandidate !== null) {
                        total = Number(totalCandidate);
                    } else if (eventsList.length > 0) {
                        total = eventsList.length;
                    }

                    const quantityCandidate = numericOrNull(
                        eventsPayload.quantity
                        ?? eventsPayload.confirmed_quantity
                        ?? eventsPayload.total_quantity
                        ?? eventsPayload.totalQuantity
                        ?? eventsPayload.quantity_total
                        ?? eventsPayload.quantityTotal
                    );
                    if (quantityCandidate !== null) {
                        quantityTotal = quantityCandidate;
                    }
                }

                if (quantityTotal === 0 && eventsList.length > 0) {
                    quantityTotal = sumConfirmedQuantities(eventsList);
                }

                if (eventsList.length === 0 && total === 0 && quantityTotal === 0) {
                    delete next[key];
                    normalizedSummary = null;
                } else {
                    const summary = {
                        events: eventsList,
                        total,
                        quantity: quantityTotal,
                    };
                    next[key] = summary;
                    normalizedSummary = summary;
                }
            }
            return next;
        });
        if (typeof onConfirmedQuantityEventsChange === "function") {
            onConfirmedQuantityEventsChange(rowId, normalizedSummary);
        }
    }, [ensureEditable, onConfirmedQuantityEventsChange]);

    const handleModalConfirmedEventsChange = useCallback((eventsPayload) => {
        if (!confirmedQuantityRowId) {
            return;
        }
        handleConfirmedQuantityEventsChange(confirmedQuantityRowId, eventsPayload);
        // eslint-disable-next-line
    }, [canManageDocumentConfirmedQuantity, confirmedQuantityRowId, handleConfirmedQuantityEventsChange, t]);

    useEffect(() => {
        if (!externalConfirmedQuantityRequest || !externalConfirmedQuantityRequest.key) {
            return;
        }
        if (externalRequestProcessedRef.current === externalConfirmedQuantityRequest.key) {
            return;
        }
        if (isBodyLoading || !Array.isArray(rows) || rows.length === 0) {
            return;
        }
        const eventData = externalConfirmedQuantityRequest.event?.rawEvent
            ?? externalConfirmedQuantityRequest.event
            ?? null;
        if (!eventData) {
            externalRequestProcessedRef.current = externalConfirmedQuantityRequest.key;
            return;
        }
        const targetRow = findRowForRequestEvent(rows, eventData);
        if (targetRow) {
            externalRequestProcessedRef.current = externalConfirmedQuantityRequest.key;
            const rowIdentifier = resolveRowIdentifier(targetRow);
            if (rowIdentifier !== null && rowIdentifier !== undefined) {
                handleOpenConfirmedQuantity(rowIdentifier);
                return;
            }
            toast.error(t("app.documents.confirmed_quantity_row_not_found", {defaultValue: "Impossibile trovare la riga richiesta"}));
            return;
        }
        externalRequestProcessedRef.current = externalConfirmedQuantityRequest.key;
        toast.error(t("app.documents.confirmed_quantity_row_not_found", {defaultValue: "Impossibile trovare la riga richiesta"}));
    }, [externalConfirmedQuantityRequest, handleOpenConfirmedQuantity, isBodyLoading, rows, t]);

    const handleImportComplete = useCallback((response) => {
        if (typeof onImportQueued === "function") {
            onImportQueued(response ?? null);
        }
        if (!resolvedCompanyId || !documentId) {
            handleCloseImport();
            return;
        }
        setBodyError(null);
        setBodyLoading(true);
        fetchDocumentBody({
            companyId: resolvedCompanyId,
            documentId,
            params: bodyFetchParams,
        })
            .then((payload) => {
                const normalized = normalizeBodyRows(payload, document, documentCurrencySymbol);
                setRows(normalized.length > 0 ? normalized : deriveRowsFromDocument(document, documentCurrencySymbol));
                setSelectedRows({});
                notifyDocumentTotalsChange();
            })
            .catch((error) => {
                setBodyError(error);
            })
            .finally(() => {
                setBodyLoading(false);
                handleCloseImport();
            });
    }, [bodyFetchParams, document, documentCurrencySymbol, documentId, handleCloseImport, notifyDocumentTotalsChange, onImportQueued, resolvedCompanyId]);

    const filteredRows = rows;

    useEffect(() => {
        if (!isExportOpen) {
            setIncludeSensitiveData(false);
            setIncludeProductDataImages(false);
            setExportError(null);
        }
    }, [isExportOpen]);

    useEffect(() => {
        documentRef.current = document;
    }, [document]);

    useEffect(() => {
        documentCurrencySymbolRef.current = documentCurrencySymbol;
    }, [documentCurrencySymbol]);

    useEffect(() => {
        const nextSnapshot = {
            id: normalizeDocumentIdentifier(document?.id ?? documentId ?? null),
            hasDocument: Boolean(document),
        };
        const previousSnapshot = documentSnapshotRef.current;
        documentSnapshotRef.current = nextSnapshot;

        if (
            nextSnapshot.id === previousSnapshot.id
            && nextSnapshot.hasDocument === previousSnapshot.hasDocument
        ) {
            return;
        }

        const hasBodyForDocument = nextSnapshot.hasDocument
            && lastBodyFetchDocumentIdRef.current === nextSnapshot.id;
        if (hasBodyForDocument) {
            return;
        }

        setRows(deriveRowsFromDocument(document, documentCurrencySymbol));
        setSelectedRows({});
        setSearchTerm("");
        setSearchResetToken(Date.now());
        lastBodyFetchDocumentIdRef.current = null;
    }, [document, documentCurrencySymbol, documentId]);

    useEffect(() => {
        if (!Array.isArray(rows)) {
            return;
        }
        setConfirmedQuantityEvents((previous) => {
            const next = {};
            const currentKeys = new Set();
            let changed = false;

            rows.forEach((row) => {
                if (!row) {
                    return;
                }
                const rawId = row.id ?? row.body_id ?? row.row_id ?? null;
                const key = rawId !== null && rawId !== undefined ? String(rawId) : "";
                if (!key) {
                    return;
                }
                currentKeys.add(key);

                const existing = previous[key];
                const derived = deriveManagedConfirmedQuantityState(row);

                if (derived) {
                    if (existing) {
                        if (managedEventsShallowEqual(existing, derived)) {
                            next[key] = existing;
                        } else {
                            next[key] = derived;
                            changed = true;
                        }
                    } else {
                        next[key] = derived;
                        changed = true;
                    }
                    return;
                }

                if (hasManagedEventsData(existing)) {
                    next[key] = existing;
                    return;
                }

                if (existing) {
                    changed = true;
                }
            });

            Object.keys(previous).forEach((key) => {
                if (!currentKeys.has(key)) {
                    changed = true;
                }
            });

            if (!changed) {
                return previous;
            }

            return next;
        });
    }, [rows]);

    useEffect(() => {
        const previous = previousCurrencyRef.current;
        if (previous.code !== documentCurrencyCode || previous.symbol !== documentCurrencySymbol) {
            setRows((currentRows) => currentRows.map((row) => {
                if (!row) {
                    return row;
                }

                const rowCurrencyCode = row.currency_code ?? null;
                const shouldUpdateCode = rowCurrencyCode === previous.code || rowCurrencyCode === null;
                const shouldUpdateSymbol = !row.currency_symbol || row.currency_symbol === previous.symbol;

                if (!shouldUpdateCode && !shouldUpdateSymbol) {
                    return row;
                }

                const nextCode = shouldUpdateCode
                    ? (documentCurrencyCode ?? rowCurrencyCode)
                    : rowCurrencyCode;
                const nextSymbol = shouldUpdateSymbol
                    ? documentCurrencySymbol
                    : row.currency_symbol;

                if (nextCode === rowCurrencyCode && nextSymbol === row.currency_symbol) {
                    return row;
                }

                return {
                    ...row,
                    currency_code: nextCode,
                    currency_symbol: nextSymbol,
                };
            }));
        }

        previousCurrencyRef.current = {
            code: documentCurrencyCode,
            symbol: documentCurrencySymbol,
        };
    }, [documentCurrencyCode, documentCurrencySymbol]);

    useEffect(() => {
        if (!isAddProductOpen) {
            setAddProductRowId(null);
        }
    }, [isAddProductOpen]);

    useEffect(() => {
        if (!resolvedCompanyId || !documentId) {
            return () => {};
        }

        const shouldNotifyTotals = importRefreshKey !== importRefreshKeyRef.current;
        importRefreshKeyRef.current = importRefreshKey;

        const controller = new AbortController();
        setBodyLoading(true);
        setBodyError(null);
        lastBodyFetchDocumentIdRef.current = null;

        fetchDocumentBody({
            companyId: resolvedCompanyId,
            documentId,
            params: bodyFetchParams,
            signal: controller.signal,
        })
            .then((payload) => {
                const currentDocument = documentRef.current;
                const currentCurrencySymbol = documentCurrencySymbolRef.current;
                const normalized = normalizeBodyRows(payload, currentDocument, currentCurrencySymbol);
                const baseRows = normalized.length > 0
                    ? normalized
                    : deriveRowsFromDocument(currentDocument, currentCurrencySymbol);
                if (!searchTermRef.current) {
                    setRows(baseRows);
                    setSelectedRows({});
                    lastBodyFetchDocumentIdRef.current = normalizeDocumentIdentifier(
                        documentId ?? (currentDocument?.id ?? null)
                    );
                }
                if (shouldNotifyTotals) {
                    notifyDocumentTotalsChange();
                }
            })
            .catch((error) => {
                if (error?.name === "AbortError") {
                    return;
                }
                setBodyError(error);
            })
            .finally(() => {
                setBodyLoading(false);
            });

        return () => {
            controller.abort();
        };
    }, [bodyFetchParams, documentId, importRefreshKey, notifyDocumentTotalsChange, resolvedCompanyId]);

    const persistCellUpdate = useCallback((rowId, columnId, apiValue, previousValue, additionalPayload) => {
        if (!resolvedCompanyId || !documentId || !rowId) {
            return;
        }

        const sanitizedExtra = additionalPayload && typeof additionalPayload === "object"
            ? Object.fromEntries(
                Object.entries(additionalPayload).filter(([, value]) => value !== undefined)
            )
            : null;

        const payload = {
            id: rowId,
            [columnId]: apiValue,
            ...(sanitizedExtra ?? {}),
        };

        if (columnId === "discount_list" && Array.isArray(apiValue)) {
            payload.discount = apiValue;
            payload.discount_string = apiValue.join("|");
        }

        if (columnId === "list_price_discounts" && Array.isArray(apiValue)) {
            payload.list_price_discounts = apiValue;
            payload.list_price_discount_string = apiValue.join("|");
        }

        if (columnId === "unit_price_discounts" && Array.isArray(apiValue)) {
            payload.unit_price_discounts = apiValue;
            payload.unit_price_discount_string = apiValue.join("|");
        }

        setUpdateError(null);

        updateDocumentBodyRow({
            companyId: resolvedCompanyId,
            documentId,
            bodyId: rowId,
            data: payload,
        })
            .then((responsePayload) => {
                setUpdateError(null);
                if (responsePayload && typeof responsePayload === "object") {
                    setRows((previousRows) => previousRows.map((row) => (
                        row.id === rowId
                            ? mergeServerRowPayload(row, responsePayload)
                            : row
                    )));
                }
                toast.success(t("app.documents.body_update_success", {defaultValue: "Riga aggiornata"}));
                notifyDocumentTotalsChange();
            })
            .catch((error) => {
                setUpdateError(error);
                setRows((previousRows) => previousRows.map((row) => {
                    if (row.id !== rowId) {
                        return row;
                    }
                    const draft = {...row};
                    if (columnId === "discount_list") {
                        draft[columnId] = normalizeDiscountList(previousValue);
                        draft.unit_price_discounts = draft[columnId];
                        if (draft.product) {
                            draft.product = {
                                ...draft.product,
                                discount: draft.discount_list,
                                unit_price_discounts: draft.discount_list,
                            };
                        }
                    } else if (columnId === "list_price_discounts") {
                        draft[columnId] = normalizeDiscountList(previousValue);
                        if (draft.product) {
                            draft.product = {
                                ...draft.product,
                                list_price_discounts: draft.list_price_discounts,
                            };
                        }
                    } else if (columnId === "unit_price_discounts") {
                        draft[columnId] = normalizeDiscountList(previousValue);
                        draft.discount_list = draft.unit_price_discounts;
                        if (draft.product) {
                            draft.product = {
                                ...draft.product,
                                discount: draft.unit_price_discounts,
                                unit_price_discounts: draft.unit_price_discounts,
                            };
                        }
                    } else {
                        draft[columnId] = previousValue;
                        if (draft.product) {
                            if (columnId === "purchase_price") {
                                draft.product = {
                                    ...draft.product,
                                    purchase_price: draft.purchase_price,
                                };
                            }
                            if (columnId === "list_price") {
                                draft.product = {
                                    ...draft.product,
                                    list_price: draft.list_price,
                                };
                            }
                            if (columnId === "unit_price") {
                                draft.product = {
                                    ...draft.product,
                                    unit_price: draft.unit_price,
                                };
                            }
                        }
                    }

                    if (["quantity", "unit_price", "unit_price_discounts", "net_price", "discount_list"].includes(columnId)) {
                        const quantity = Number(draft.quantity) || 0;
                        const unitPrice = Number(draft.unit_price) || 0;
                        const currentNetPrice = Number(draft.net_price ?? 0);
                        const recalculatedNetPrice = columnId === "net_price"
                            ? currentNetPrice
                            : deriveNetPrice(unitPrice, draft.unit_price_discounts ?? draft.discount_list ?? [], currentNetPrice);
                        draft.net_price = recalculatedNetPrice;
                        draft.total_net_price = calculateTotalFromNetPrice(quantity, recalculatedNetPrice);
                        draft.discount_price = recalculatedNetPrice;
                        draft.total = draft.total_net_price;
                    }
                    return draft;
                }));
            });
    }, [documentId, notifyDocumentTotalsChange, resolvedCompanyId, t]);

    const handleCellChange = useCallback((rowId, columnId, rawValue, options = null) => {
        if (!ensureEditable()) {
            return;
        }
        const commitOptions = options && typeof options === "object" ? options : {};
        const forceListPricePayloadFromOptions = Boolean(commitOptions.forceListPricePayload);
        const forcedPayloadValues = commitOptions.forcedPayloadValues && typeof commitOptions.forcedPayloadValues === "object"
            ? commitOptions.forcedPayloadValues
            : null;
        const shouldForceListPayload = forceListPricePayloadFromOptions || columnId === "purchase_price";
        let shouldPersist = true;
        let previousValueRef;
        let nextNetPrice;
        let previousNetPrice;
        let nextPurchasePrice;
        let previousPurchasePrice;
        let nextListPrice;
        let previousListPrice;
        let nextListPriceDiscounts;
        let previousListPriceDiscounts;
        let nextDeliveryDate;
        let appliedDeliveryFallback = false;
        let forcedPurchasePriceValue;
        let forcedListPriceValue;
        let forcedListPriceDiscountsValue;

        setRows((previousRows) => {
            const next = [...previousRows];
            const index = next.findIndex((item) => item.id === rowId);
            if (index === -1) {
                shouldPersist = false;
                return previousRows;
            }

            const prevRow = next[index];
            previousValueRef = prevRow[columnId];
            previousNetPrice = prevRow?.net_price;
            previousPurchasePrice = prevRow?.purchase_price;
            previousListPrice = prevRow?.list_price;
            previousListPriceDiscounts = prevRow?.list_price_discounts;

            const draft = {...prevRow};

            if (columnId === "discount_list") {
                const normalized = normalizeDiscountList(rawValue);
                draft.discount_list = normalized;
                draft.unit_price_discounts = normalized;
                draft[columnId] = normalized;
            } else if (columnId === "list_price_discounts") {
                draft.list_price_discounts = normalizeDiscountList(rawValue);
            } else if (columnId === "unit_price_discounts") {
                const normalized = normalizeDiscountList(rawValue);
                draft.unit_price_discounts = normalized;
                draft.discount_list = normalized;
            } else if (NUMERIC_FIELDS.has(columnId)) {
                if (columnId === "net_price" && (rawValue === "" || rawValue === null || rawValue === undefined)) {
                    draft[columnId] = null;
                } else {
                    const numeric = Number(rawValue);
                    draft[columnId] = Number.isNaN(numeric) ? 0 : numeric;
                }
            } else {
                draft[columnId] = rawValue ?? (columnId === "picture" ? null : "");
            }

            if ((columnId === "discount_list" || columnId === "unit_price_discounts") && draft.product) {
                draft.product = {
                    ...draft.product,
                    discount: draft.unit_price_discounts,
                    unit_price_discounts: draft.unit_price_discounts,
                };
            }

            if (columnId === "list_price_discounts" && draft.product) {
                draft.product = {
                    ...draft.product,
                    list_price_discounts: draft.list_price_discounts,
                };
            }

            if (columnId === "list_price" && draft.product) {
                draft.product = {
                    ...draft.product,
                    list_price: draft.list_price,
                };
            }

            if (columnId === "unit_price" && draft.product) {
                draft.product = {
                    ...draft.product,
                    unit_price: draft.unit_price,
                };
            }

            if (columnId === "net_price" && draft.product) {
                draft.product = {
                    ...draft.product,
                    net_price: draft.net_price,
                };
            }

            if (columnId === "purchase_price") {
                const numericPurchasePrice = Number(draft.purchase_price);
                const sanitizedPurchasePrice = Number.isNaN(numericPurchasePrice)
                    ? 0
                    : Number(numericPurchasePrice.toFixed(4));
                if (sanitizedPurchasePrice !== draft.purchase_price) {
                    draft.purchase_price = sanitizedPurchasePrice;
                }
                nextPurchasePrice = draft.purchase_price;

                if (draft.product) {
                    draft.product = {
                        ...draft.product,
                        purchase_price: draft.purchase_price,
                    };
                }

                if (draft.list_price !== draft.purchase_price) {
                    draft.list_price = draft.purchase_price;
                    nextListPrice = draft.list_price;
                    if (draft.product) {
                        draft.product = {
                            ...draft.product,
                            list_price: draft.list_price,
                        };
                    }
                }

                const normalizedPreviousListDiscounts = normalizeDiscountList(previousListPriceDiscounts);
                const normalizedCurrentListDiscounts = normalizeDiscountList(draft.list_price_discounts);
                const shouldResetListDiscounts = normalizedPreviousListDiscounts.length > 0 || normalizedCurrentListDiscounts.length > 0;
                if (shouldResetListDiscounts) {
                    draft.list_price_discounts = [];
                    nextListPriceDiscounts = draft.list_price_discounts;
                    if (draft.product) {
                        draft.product = {
                            ...draft.product,
                            list_price_discounts: draft.list_price_discounts,
                        };
                    }
                }
            }

            if (columnId === "list_price" || columnId === "list_price_discounts") {
                let listDiscounts = draft.list_price_discounts;
                if (!Array.isArray(listDiscounts)) {
                    listDiscounts = normalizeDiscountList(listDiscounts);
                    draft.list_price_discounts = listDiscounts;
                }
                const hasListDiscounts = listDiscounts.length > 0;
                let computedPurchasePrice;

                if (columnId === "list_price" && !hasListDiscounts) {
                    computedPurchasePrice = Number(draft.list_price) || 0;
                } else if (columnId === "list_price_discounts") {
                    computedPurchasePrice = applyDiscountsToValue(draft.list_price, listDiscounts);
                }

                if (computedPurchasePrice !== undefined) {
                    const numericPurchasePrice = Number(computedPurchasePrice);
                    const sanitizedPurchasePrice = Number.isNaN(numericPurchasePrice)
                        ? 0
                        : Number(numericPurchasePrice.toFixed(4));
                    if (sanitizedPurchasePrice !== draft.purchase_price) {
                        draft.purchase_price = sanitizedPurchasePrice;
                        nextPurchasePrice = draft.purchase_price;
                        if (draft.product) {
                            draft.product = {
                                ...draft.product,
                                purchase_price: draft.purchase_price,
                            };
                        }
                    }
                }
            }

            if (["quantity", "unit_price", "unit_price_discounts", "discount_list", "net_price"].includes(columnId)) {
                const quantity = columnId === "quantity"
                    ? (Number(rawValue) || 0)
                    : Number(draft.quantity) || 0;
                const unitPrice = columnId === "unit_price"
                    ? (Number(rawValue) || 0)
                    : Number(draft.unit_price) || 0;

                if (columnId === "unit_price" || columnId === "unit_price_discounts" || columnId === "discount_list") {
                    draft.net_price = deriveNetPrice(unitPrice, draft.unit_price_discounts ?? draft.discount_list ?? [], null);
                    if (draft.product) {
                        draft.product = {
                            ...draft.product,
                            net_price: draft.net_price,
                        };
                    }
                }

                if (columnId === "net_price" && (rawValue === "" || rawValue === null || rawValue === undefined)) {
                    draft.net_price = deriveNetPrice(unitPrice, draft.unit_price_discounts ?? draft.discount_list ?? [], null);
                }

                const effectiveNetPrice = deriveNetPrice(
                    unitPrice,
                    draft.unit_price_discounts ?? draft.discount_list ?? [],
                    draft.net_price
                );

                draft.net_price = effectiveNetPrice;
                draft.total_net_price = calculateTotalFromNetPrice(quantity, effectiveNetPrice);
                draft.discount_price = effectiveNetPrice;
                draft.total = draft.total_net_price;
                nextNetPrice = draft.net_price;
            }

            if (columnId !== "delivery_date" && !draft.delivery_date && documentDeliveryDateRef.current) {
                draft.delivery_date = documentDeliveryDateRef.current;
                appliedDeliveryFallback = true;
            }

            nextDeliveryDate = draft.delivery_date;
            next[index] = draft;
            if (shouldForceListPayload) {
                forcedPurchasePriceValue = forcedPayloadValues?.purchase_price ?? draft.purchase_price;
                forcedListPriceValue = forcedPayloadValues?.list_price ?? draft.list_price;
                const forcedDiscountSource = forcedPayloadValues?.list_price_discounts ?? draft.list_price_discounts;
                forcedListPriceDiscountsValue = normalizeDiscountList(forcedDiscountSource);
            }
            return next;
        });

        if (!shouldPersist) {
            return;
        }

        const apiValue = prepareApiValue(columnId, rawValue);
        const previousApiValue = prepareApiValue(columnId, previousValueRef);

        const serializedApiValue = JSON.stringify(apiValue);
        const serializedPreviousApiValue = JSON.stringify(previousApiValue);

        const previousNetPriceApiValue = previousNetPrice !== undefined
            ? prepareApiValue("net_price", previousNetPrice)
            : undefined;
        const nextNetPriceApiValue = nextNetPrice !== undefined
            ? prepareApiValue("net_price", nextNetPrice)
            : undefined;
        const shouldSendNetPrice = (columnId === "unit_price" || columnId === "unit_price_discounts")
            && nextNetPriceApiValue !== undefined
            && nextNetPriceApiValue !== previousNetPriceApiValue;
        const previousPurchasePriceApiValue = previousPurchasePrice !== undefined
            ? prepareApiValue("purchase_price", previousPurchasePrice)
            : undefined;
        const nextPurchasePriceApiValue = nextPurchasePrice !== undefined
            ? prepareApiValue("purchase_price", nextPurchasePrice)
            : undefined;
        const shouldSendPurchasePrice = nextPurchasePriceApiValue !== undefined
            && nextPurchasePriceApiValue !== previousPurchasePriceApiValue;
        const previousListPriceApiValue = previousListPrice !== undefined
            ? prepareApiValue("list_price", previousListPrice)
            : undefined;
        const nextListPriceApiValue = nextListPrice !== undefined
            ? prepareApiValue("list_price", nextListPrice)
            : undefined;
        const shouldSendListPrice = nextListPriceApiValue !== undefined
            && nextListPriceApiValue !== previousListPriceApiValue;
        const previousListPriceDiscountsApiValue = previousListPriceDiscounts !== undefined
            ? prepareApiValue("list_price_discounts", previousListPriceDiscounts)
            : undefined;
        const nextListPriceDiscountsApiValue = nextListPriceDiscounts !== undefined
            ? prepareApiValue("list_price_discounts", nextListPriceDiscounts)
            : undefined;
        const shouldSendListPriceDiscounts = nextListPriceDiscountsApiValue !== undefined
            && nextListPriceDiscountsApiValue !== previousListPriceDiscountsApiValue;

        const additionalPayload = {};
        if (shouldSendNetPrice) {
            additionalPayload.net_price = nextNetPriceApiValue;
        }
        if (shouldSendPurchasePrice) {
            additionalPayload.purchase_price = nextPurchasePriceApiValue;
        }
        if (shouldSendListPrice) {
            additionalPayload.list_price = nextListPriceApiValue;
        }
        if (shouldSendListPriceDiscounts) {
            additionalPayload.list_price_discounts = nextListPriceDiscountsApiValue;
            if (Array.isArray(nextListPriceDiscountsApiValue)) {
                additionalPayload.list_price_discount_string = nextListPriceDiscountsApiValue.join("|");
            }
        }
        const hasComputedPayload = Object.keys(additionalPayload).length > 0;
        const hasValueChange = serializedApiValue !== serializedPreviousApiValue;

        const hasForcedPayload = shouldForceListPayload
            && (
                forcedPurchasePriceValue !== undefined
                || forcedListPriceValue !== undefined
                || forcedListPriceDiscountsValue !== undefined
            );

        if (!hasValueChange && !hasComputedPayload && !hasForcedPayload) {
            return;
        }

        if (hasForcedPayload) {
            if (forcedPurchasePriceValue !== undefined && columnId !== "purchase_price") {
                additionalPayload.purchase_price = prepareApiValue("purchase_price", forcedPurchasePriceValue);
            }
            if (forcedListPriceValue !== undefined && columnId !== "list_price") {
                additionalPayload.list_price = prepareApiValue("list_price", forcedListPriceValue);
            }
            if (forcedListPriceDiscountsValue !== undefined && columnId !== "list_price_discounts") {
                const forcedListPriceDiscountsApiValue = prepareApiValue("list_price_discounts", forcedListPriceDiscountsValue);
                additionalPayload.list_price_discounts = forcedListPriceDiscountsApiValue;
                if (Array.isArray(forcedListPriceDiscountsApiValue)) {
                    additionalPayload.list_price_discount_string = forcedListPriceDiscountsApiValue.join("|");
                }
            }
        }

        let effectiveDeliveryDate = nextDeliveryDate || documentDeliveryDateRef.current || null;
        if (appliedDeliveryFallback && documentDeliveryDateRawRef.current) {
            effectiveDeliveryDate = documentDeliveryDateRawRef.current;
        }
        if (columnId !== "delivery_date") {
            additionalPayload.delivery_date = prepareApiValue("delivery_date", effectiveDeliveryDate);
        }

        const hasAdditionalPayload = Object.keys(additionalPayload).length > 0;

        persistCellUpdate(
            rowId,
            columnId,
            apiValue,
            previousValueRef,
            hasAdditionalPayload ? additionalPayload : undefined
        );
    }, [ensureEditable, persistCellUpdate]);

    const handleToggleRow = useCallback((rowId) => {
        setSelectedRows((previousSelected) => ({
            ...previousSelected,
            [rowId]: !previousSelected[rowId],
        }));
    }, []);

    const handleToggleAllRows = useCallback(() => {
        if (rows.length === 0) {
            return;
        }
        const areAllSelected = rows.every((row) => selectedRows[row.id]);
        if (areAllSelected) {
            setSelectedRows({});
            return;
        }
        const mapped = rows.reduce((accumulator, row) => {
            accumulator[row.id] = true;
            return accumulator;
        }, {});
        setSelectedRows(mapped);
    }, [rows, selectedRows]);

    const clearSelectionForIds = useCallback((ids) => {
        if (!ids) {
            return;
        }
        let pending = [];
        if (ids instanceof Set) {
            pending = Array.from(ids);
        } else if (Array.isArray(ids)) {
            pending = [...ids];
        } else if (ids) {
            pending = [ids];
        }
        if (pending.length === 0) {
            return;
        }
        const normalizedIds = pending
            .map((value) => {
                if (value === null || value === undefined) {
                    return "";
                }
                return String(value);
            })
            .filter(Boolean);
        if (normalizedIds.length === 0) {
            return;
        }
        setSelectedRows((previousSelected) => {
            if (!previousSelected || Object.keys(previousSelected).length === 0) {
                return previousSelected;
            }
            const nextSelection = {...previousSelected};
            let mutated = false;
            normalizedIds.forEach((rowId) => {
                if (nextSelection[rowId]) {
                    delete nextSelection[rowId];
                    mutated = true;
                }
            });
            return mutated ? nextSelection : previousSelected;
        });
    }, [setSelectedRows]);

    const immediateRemoveRow = useCallback((rowId) => {
        if (!rowId) {
            return;
        }
        setRows((previousRows) => previousRows.filter((row) => row.id !== rowId));
        setSelectedRows((previousSelected) => {
            if (!previousSelected[rowId]) {
                return previousSelected;
            }
            const next = {...previousSelected};
            delete next[rowId];
            return next;
        });
    }, []);

    const scrollToLatestRow = useCallback(() => {
        const queueRetry = () => {
            if (scrollAttemptRef.current >= MAX_SCROLL_ATTEMPTS) {
                scrollAttemptRef.current = 0;
                return;
            }
            scrollAttemptRef.current += 1;
            requestAnimationFrame(scrollToLatestRow);
        };

        const browserDocument = typeof window !== "undefined" ? window.document : null;
        const container = tableWrapperRef.current ?? browserDocument;
        if (!container) {
            queueRetry();
            return;
        }

        const targetRowId = pendingScrollRowIdRef.current ? String(pendingScrollRowIdRef.current) : null;
        let targetRow = null;
        if (targetRowId) {
            const selector = `[data-row-id="${escapeRowSelector(targetRowId)}"]`;
            targetRow = container.querySelector?.(selector)
                ?? browserDocument?.querySelector?.(selector)
                ?? null;
        }

        if (!targetRow) {
            const droppable = container.querySelector?.('[data-rbd-droppable-id^="editable-table-document-body"]');
            if (droppable) {
                const tableRows = droppable.querySelectorAll("tr");
                if (tableRows.length > 0) {
                    targetRow = tableRows[tableRows.length - 1];
                }
            }
        }

        if (!targetRow) {
            targetRow = container.querySelector?.("tbody tr:last-child") ?? container.querySelector?.("table tr:last-child");
        }

        if (!targetRow) {
            const scrollHost = tableWrapperRef.current?.querySelector?.("[data-table-scroll-container=\"true\"]")
                ?? container.querySelector?.("[data-table-scroll-container=\"true\"]");
            if (scrollHost) {
                if (typeof scrollHost.scrollTo === "function") {
                    scrollHost.scrollTo({top: scrollHost.scrollHeight, behavior: "auto"});
                } else {
                    scrollHost.scrollTop = scrollHost.scrollHeight;
                }
            }
            queueRetry();
            return;
        }

        const finalizeSuccess = () => {
            scrollAttemptRef.current = 0;
            pendingScrollRowIdRef.current = null;
        };

        const scrollContainer = findScrollableParent(targetRow, tableWrapperRef.current);
        const behavior = scrollAttemptRef.current <= 1 ? "smooth" : "auto";
        if (scrollContainer) {
            if (typeof scrollContainer.scrollTo === "function") {
                scrollContainer.scrollTo({top: scrollContainer.scrollHeight, behavior});
            } else {
                scrollContainer.scrollTop = scrollContainer.scrollHeight;
            }
            const containerRect = scrollContainer.getBoundingClientRect();
            const rowRect = targetRow.getBoundingClientRect();
            const isRowVisible = rowRect.bottom <= containerRect.bottom + 1;
            if (isRowVisible) {
                finalizeSuccess();
                return;
            }
        } else if (typeof targetRow.scrollIntoView === "function") {
            targetRow.scrollIntoView({behavior, block: "end"});
            const viewportHeight = typeof window !== "undefined"
                ? (window.innerHeight || window.document?.documentElement?.clientHeight || 0)
                : 0;
            if (viewportHeight > 0) {
                const rowRect = targetRow.getBoundingClientRect();
                if (rowRect.bottom <= viewportHeight + 1) {
                    finalizeSuccess();
                    return;
                }
            }
        } else {
            finalizeSuccess();
            return;
        }

        queueRetry();
    }, []);

    useEffect(() => {
        if (!shouldScrollRef.current) {
            return;
        }
        shouldScrollRef.current = false;
        scrollAttemptRef.current = 0;
        requestAnimationFrame(() => {
            requestAnimationFrame(scrollToLatestRow);
        });
    }, [rows, scrollToLatestRow]);

    const handleAddRow = useCallback(() => {
        if (!canCreateDocumentBody) {
            toast.error(t("app.common.permission_denied", {defaultValue: "Operazione non consentita"}));
            return;
        }
        if (!ensureEditable()) {
            return;
        }
        if (isCreatingRow) {
            return;
        }

        if (!resolvedCompanyId || !documentId) {
            const newRowId = `new-${Date.now()}`;
            setRows((previousRows) => ([
                ...previousRows,
                {
                    id: newRowId,
                    name: "",
                    picture: null,
                    product: null,
                    product_reference: "",
                    delivery_date: documentDeliveryDateRef.current || "",
                    note: "",
                    quantity: 0,
                    list_price: 0,
                    list_price_discounts: [],
                    unit_price: 0,
                    unit_price_discounts: [],
                    discount_list: [],
                    purchase_price: 0,
                    net_price: 0,
                    total_net_price: 0,
                    discount_price: 0,
                    total: 0,
                    volume: 0,
                    gross_weight: 0,
                    net_weight: 0,
                    qty_per_packaging: 0,
                    length: 0,
                    width: 0,
                    height: 0,
                    radius: 0,
                    currency_symbol: documentCurrencySymbol,
                    currency_code: documentCurrencyCode,
                    position: (previousRows?.length ?? 0) + 1,
                },
            ]));
            pendingScrollRowIdRef.current = newRowId;
            shouldScrollRef.current = true;
            return;
        }

        setIsCreatingRow(true);
        setBodyError(null);

        createDocumentBodyRow({
            companyId: resolvedCompanyId,
            documentId,
            data: {},
        })
            .then(() => fetchDocumentBody({companyId: resolvedCompanyId, documentId, params: bodyFetchParams}))
            .then((payload) => {
                const normalized = normalizeBodyRows(payload, document, documentCurrencySymbol);
                const nextRows = normalized.length > 0
                    ? normalized
                    : deriveRowsFromDocument(document, documentCurrencySymbol);

                let appendedRowId = null;
                setRows((previousRows) => {
                    if (!Array.isArray(previousRows) || previousRows.length === 0) {
                        appendedRowId = nextRows?.[nextRows.length - 1]?.id ?? null;
                        return nextRows;
                    }

                    const nextMap = new Map(nextRows.map((row) => [row.id, row]));
                    const merged = previousRows
                        .map((row) => nextMap.get(row.id) ?? row)
                        .filter((row) => nextMap.has(row.id));
                    const existingIds = new Set(merged.map((row) => row.id));
                    const appended = nextRows.filter((row) => !existingIds.has(row.id));
                    if (appended.length > 0) {
                        const lastAppended = appended[appended.length - 1];
                        appendedRowId = lastAppended ? (lastAppended.id ?? null) : null;
                    } else if (nextRows.length > 0) {
                        appendedRowId = nextRows[nextRows.length - 1]?.id ?? null;
                    }
                    return [...merged, ...appended];
                });

                setSelectedRows({});
                toast.success(t("app.documents.body_create_success", {defaultValue: "Riga creata"}));
                const resolvedScrollId = appendedRowId ?? (nextRows.length > 0 ? nextRows[nextRows.length - 1]?.id ?? null : null);
                pendingScrollRowIdRef.current = resolvedScrollId ?? null;
                shouldScrollRef.current = true;
            })
            .catch((error) => {
                if (error?.name === "AbortError") {
                    return;
                }
                setBodyError(error);
            })
            .finally(() => {
                setIsCreatingRow(false);
            });
    }, [bodyFetchParams, canCreateDocumentBody, document, documentCurrencyCode, documentCurrencySymbol, documentId, ensureEditable, isCreatingRow, resolvedCompanyId, t]);

    const handleOpenCatalogPanel = useCallback(() => {
        if (!canAccessCatalog) {
            toast.error(t("app.common.permission_denied", {defaultValue: "Operazione non consentita"}));
            return;
        }
        if (!ensureEditable()) {
            return;
        }
        if (!resolvedCompanyId || !documentId) {
            toast.error(t("app.common.generic_error", {defaultValue: "Impossibile aprire il catalogo. Salva il documento e riprova."}));
            return;
        }
        setIsCatalogPanelOpen(true);
    }, [canAccessCatalog, documentId, ensureEditable, resolvedCompanyId, t]);

    const handleCloseCatalogPanel = useCallback(() => {
        setIsCatalogPanelOpen(false);
        setIsCatalogSubmitting(false);
    }, []);

    const handleCatalogBulkInsert = useCallback((items) => {
        if (!ensureEditable()) {
            return;
        }
        if (!Array.isArray(items) || items.length === 0) {
            return;
        }
        if (!resolvedCompanyId || !documentId) {
            toast.error(t("app.common.generic_error", {defaultValue: "Impossibile aggiungere i prodotti. Salva il documento e riprova."}));
            return;
        }

        setIsCatalogSubmitting(true);
        setBodyError(null);

        insertDocumentBodyRows({
            companyId: resolvedCompanyId,
            documentId,
            bodies: items,
        })
            .then(() => fetchDocumentBody({companyId: resolvedCompanyId, documentId, params: bodyFetchParams}))
            .then((payload) => {
                const normalized = normalizeBodyRows(payload, document, documentCurrencySymbol);
                const nextRows = normalized.length > 0
                    ? normalized
                    : deriveRowsFromDocument(document, documentCurrencySymbol);
                setRows(nextRows);
                setSelectedRows({});
                pendingScrollRowIdRef.current = nextRows.length > 0
                    ? nextRows[nextRows.length - 1]?.id ?? null
                    : null;
                shouldScrollRef.current = true;
                toast.success(t("app.documents.catalog_insert_success", {defaultValue: "Prodotti aggiunti al documento"}));
                setIsCatalogPanelOpen(false);
                notifyDocumentTotalsChange();
            })
            .catch((error) => {
                if (error?.name === "AbortError") {
                    return;
                }
                setBodyError(error);
                toast.error(t("app.common.generic_error", {defaultValue: "Impossibile aggiungere i prodotti. Riprova."}));
            })
            .finally(() => {
                setIsCatalogSubmitting(false);
            });
    }, [bodyFetchParams, document, documentCurrencySymbol, documentId, ensureEditable, notifyDocumentTotalsChange, resolvedCompanyId, t]);

    const handleOpenProductCreate = useCallback((rowId) => {
        if (!canCreateProduct) {
            toast.error(t("app.common.permission_denied", {defaultValue: "Operazione non consentita"}));
            return;
        }
        if (!ensureEditable()) {
            return;
        }
        if (!rowId || !resolvedCompanyId) {
            return;
        }
        setAddProductRowId(rowId);
        setIsAddProductOpen(true);
    }, [canCreateProduct, ensureEditable, resolvedCompanyId, t]);

    const handleOpenProductEdit = useCallback((rowId, rowProduct) => {
        if (!canEditProduct || !ensureEditable()) {
            return;
        }
        if (!rowId || !resolvedCompanyId) {
            return;
        }
        const target = rowProduct?.product ?? rowProduct;
        if (!target?.id) {
            return;
        }
        setEditProductRowId(rowId);
        setEditProductId(target.id);
        setIsEditProductOpen(true);
    }, [canEditProduct, ensureEditable, resolvedCompanyId]);

    const handleOpenAddRate = useCallback(({product: targetProduct, presetValues = null}) => {
        if (!canCreateProductRate || !targetProduct?.id) {
            return;
        }
        setAddRateProduct(targetProduct);
        setAddRateDefaults(presetValues);
        setAddRateError(null);
        setIsAddRateOpen(true);
    }, [canCreateProductRate]);

    const handleCloseAddRate = useCallback(() => {
        if (isAddRateSubmitting) {
            return;
        }
        setIsAddRateOpen(false);
        setAddRateProduct(null);
        setAddRateDefaults(null);
        setAddRateError(null);
    }, [isAddRateSubmitting]);

    const handleAddRateSubmit = useCallback((payload) => {
        if (!addRateProduct?.id) {
            return;
        }
        setIsAddRateSubmitting(true);
        setAddRateError(null);
        postElement(getPathParam([PRODUCT, addRateProduct.id, PRICE_HISTORY]), payload)
            .then(() => {
                toast.success(t("app.products.update_success", {defaultValue: "Prodotto aggiornato"}));
                setIsAddRateOpen(false);
                setAddRateProduct(null);
                setAddRateDefaults(null);
            })
            .catch((error) => {
                if (error?.name === "AbortError") {
                    return;
                }
                setAddRateError(error?.message ?? error?.detail ?? t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"}));
            })
            .finally(() => {
                setIsAddRateSubmitting(false);
            });
    }, [addRateProduct?.id, t]);

    const handleOpenPurchaseHistory = useCallback((rowId) => {
        if (!canViewPurchaseHistory || !rowId) {
            return;
        }
        setPurchaseHistoryRowId(rowId);
        setIsPurchaseHistoryOpen(true);
    }, [canViewPurchaseHistory]);

    const handleClosePurchaseHistory = useCallback(() => {
        setIsPurchaseHistoryOpen(false);
        setPurchaseHistoryRowId(null);
    }, []);

    const handlePurchaseHistoryListPriceCommit = useCallback((nextValue, forcedValues = null) => {
        if (!purchaseHistoryRowId) {
            return;
        }
        handleCellChange(
            purchaseHistoryRowId,
            "list_price",
            nextValue,
            {forceListPricePayload: true, forcedPayloadValues: forcedValues}
        );
    }, [handleCellChange, purchaseHistoryRowId]);

    const handlePurchaseHistoryDiscountsCommit = useCallback((nextValue, forcedValues = null) => {
        if (!purchaseHistoryRowId) {
            return;
        }
        handleCellChange(
            purchaseHistoryRowId,
            "list_price_discounts",
            nextValue,
            {forceListPricePayload: true, forcedPayloadValues: forcedValues}
        );
    }, [handleCellChange, purchaseHistoryRowId]);

    const handlePurchaseHistoryAddCatalog = useCallback(() => {
        if (!purchaseHistoryRowId) {
            return;
        }
        const targetRow = rows.find((row) => row.id === purchaseHistoryRowId);
        if (!targetRow?.product?.id) {
            return;
        }
        const discountSource = normalizeDiscountList(
            targetRow.list_price_discounts
            ?? targetRow.product?.list_price_discounts
            ?? []
        );
        const discountString = discountSource.length > 0
            ? discountSource.map((entry) => Number(entry)).filter((entry) => Number.isFinite(entry)).join("|")
            : "";
        handleOpenAddRate({
            product: targetRow.product,
            presetValues: {
                purchase_price: targetRow.list_price ?? targetRow.product?.list_price ?? "",
                discount_string: discountString,
            },
        });
        setIsPurchaseHistoryOpen(false);
        setPurchaseHistoryRowId(null);
    }, [handleOpenAddRate, purchaseHistoryRowId, rows]);

    const resolveProductDetails = useCallback(async (product) => {
        if (!product?.id || !resolvedCompanyId) {
            return product;
        }
        if (Array.isArray(product?.product_dimensions) && product.product_dimensions.length > 0) {
            return product;
        }
        try {
            const detailed = await getProduct({company_id: resolvedCompanyId, product_id: product.id});
            if (detailed) {
                return {...product, ...detailed};
            }
        } catch (error) {
            if (error?.name === "AbortError") {
                return product;
            }
        }
        return product;
    }, [resolvedCompanyId]);

    const handleAssignProduct = useCallback((rowId, product, options = {}) => {
        if (!ensureEditable()) {
            return;
        }
        if (!rowId || !product?.id) {
            return;
        }
        const applyUnitPricing = options?.applyUnitPricing !== false;
        const preserveExistingPricing = options?.preserveExistingPricing === true;

        let previousSnapshot = null;
        let nextPayload = null;
        const pictureAsset = Array.isArray(product?.pictures)
            ? product.pictures.find((asset) => asset?.reference)?.reference
            : product?.picture?.reference ?? null;

        setRows((previousRows) => previousRows.map((row) => {
            if (row.id !== rowId) {
                return row;
            }
            previousSnapshot = row;
            const fallbackProduct = row.product ?? {
                purchase_price: row.purchase_price,
                list_price: row.list_price,
                list_price_discounts: row.list_price_discounts,
                unit_price: row.unit_price,
                unit_price_discounts: row.unit_price_discounts ?? row.discount_list,
                net_price: row.net_price,
                product_reference: row.product_reference,
                name: row.name,
            };
            const defaultPackaging = Array.isArray(product?.product_dimensions)
                ? product.product_dimensions.find((dimension) => dimension.default_dimension)
                : null;
            const resolvedVolume = defaultPackaging?.volume ?? row.volume ?? 0;
            const resolvedGrossWeight = defaultPackaging?.gross_weight ?? row.gross_weight ?? 0;
            const resolvedNetWeight = defaultPackaging?.net_weight ?? row.net_weight ?? 0;
            const resolvedQtyPerPackaging = defaultPackaging?.qty_per_packaging ?? row.qty_per_packaging ?? 0;
            const resolvedLength = defaultPackaging?.length ?? row.length ?? 0;
            const resolvedWidth = defaultPackaging?.width ?? row.width ?? 0;
            const resolvedHeight = defaultPackaging?.height ?? row.height ?? 0;
            const resolvedRadius = defaultPackaging?.radius ?? row.radius ?? 0;

            const updatedRow = {
                ...row,
                name: product?.name ?? row.name ?? "",
                product_reference: product?.product_reference ?? row.product_reference ?? "",
                manufacture_reference: product?.manufacture_reference ?? row.manufacture_reference ?? "",
                picture: pictureAsset ?? row.picture ?? null,
                product: normalizeRowProduct(product, fallbackProduct),
                volume: Number(resolvedVolume ?? 0),
                gross_weight: Number(resolvedGrossWeight ?? 0),
                net_weight: Number(resolvedNetWeight ?? 0),
                qty_per_packaging: Number(resolvedQtyPerPackaging ?? 0),
                length: Number(resolvedLength ?? 0),
                width: Number(resolvedWidth ?? 0),
                height: Number(resolvedHeight ?? 0),
                radius: Number(resolvedRadius ?? 0),
            };
            const normalizedProduct = updatedRow.product ?? normalizeRowProduct(product, fallbackProduct);
            const rowUnitPrice = row.unit_price ?? 0;
            const rowUnitDiscounts = row.unit_price_discounts ?? row.discount_list ?? [];
            const hasListPriceValue = preserveExistingPricing
                && row.list_price !== null
                && row.list_price !== undefined
                && row.list_price !== "";
            const hasListDiscountsValue = preserveExistingPricing
                && Array.isArray(row.list_price_discounts)
                && row.list_price_discounts.length > 0;
            const hasUnitPriceValue = preserveExistingPricing
                && row.unit_price !== null
                && row.unit_price !== undefined
                && row.unit_price !== "";
            const hasUnitDiscountsValue = preserveExistingPricing
                && Array.isArray(rowUnitDiscounts)
                && rowUnitDiscounts.length > 0;
            const resolvedListPrice = hasListPriceValue
                ? row.list_price
                : normalizedProduct?.list_price ?? product?.list_price ?? row.list_price ?? 0;
            const resolvedListDiscounts = hasListDiscountsValue
                ? row.list_price_discounts
                : normalizedProduct?.list_price_discounts
                    ?? product?.list_price_discounts
                    ?? row.list_price_discounts
                    ?? [];
            const resolvedUnitPrice = hasUnitPriceValue
                ? rowUnitPrice
                : (
                    applyUnitPricing
                        ? normalizedProduct?.unit_price ?? product?.unit_price ?? rowUnitPrice ?? 0
                        : rowUnitPrice ?? 0
                );
            const resolvedUnitDiscounts = normalizedProduct?.unit_price_discounts
                ?? product?.unit_price_discounts
                ?? product?.discount
                ?? rowUnitDiscounts
                ?? [];
            const nextUnitDiscounts = hasUnitDiscountsValue
                ? rowUnitDiscounts
                : (applyUnitPricing ? resolvedUnitDiscounts : rowUnitDiscounts);
            const resolvedPurchasePrice = normalizedProduct?.purchase_price
                ?? product?.purchase_price
                ?? row.purchase_price
                ?? 0;
            const baseNetPrice = row.net_price;
            const explicitNetPrice = applyUnitPricing
                ? normalizedProduct?.net_price ?? product?.net_price ?? baseNetPrice
                : baseNetPrice;

            updatedRow.list_price = Number(resolvedListPrice ?? 0);
            const normalizedListDiscounts = normalizeDiscountList(resolvedListDiscounts);
            updatedRow.list_price_discounts = normalizedListDiscounts;
            updatedRow.unit_price = Number(resolvedUnitPrice ?? 0);
            const normalizedUnitDiscounts = normalizeDiscountList(nextUnitDiscounts);
            updatedRow.unit_price_discounts = normalizedUnitDiscounts;
            updatedRow.discount_list = normalizedUnitDiscounts;
            updatedRow.purchase_price = Number(resolvedPurchasePrice ?? 0);
            let updatedRowDeliveryDate = updatedRow.delivery_date;
            let updatedRowDeliveryDatePayload = updatedRowDeliveryDate;
            if (!updatedRowDeliveryDate && documentDeliveryDateRef.current) {
                updatedRowDeliveryDate = documentDeliveryDateRef.current;
                updatedRowDeliveryDatePayload = documentDeliveryDateRawRef.current || documentDeliveryDateRef.current;
            }
            updatedRow.delivery_date = updatedRowDeliveryDate;

            const computedNetPrice = deriveNetPrice(
                updatedRow.unit_price,
                updatedRow.unit_price_discounts,
                explicitNetPrice
            );
            updatedRow.net_price = computedNetPrice;
            updatedRow.total_net_price = calculateTotalFromNetPrice(updatedRow.quantity, computedNetPrice);
            updatedRow.discount_price = computedNetPrice;
            updatedRow.total = updatedRow.total_net_price;
            const listPriceDiscountString = Array.isArray(normalizedListDiscounts)
                ? normalizedListDiscounts.join("|")
                : "";
            const unitPriceDiscountString = Array.isArray(normalizedUnitDiscounts)
                ? normalizedUnitDiscounts.join("|")
                : "";
            nextPayload = {
                id: rowId,
                product: {id: product.id},
                list_price: updatedRow.list_price,
                list_price_discounts: normalizedListDiscounts,
                list_price_discount_string: listPriceDiscountString,
                unit_price: updatedRow.unit_price,
                unit_price_discounts: normalizedUnitDiscounts,
                unit_price_discount_string: unitPriceDiscountString,
                discount_list: normalizedUnitDiscounts,
                discount: normalizedUnitDiscounts,
                discount_string: unitPriceDiscountString,
                purchase_price: updatedRow.purchase_price,
                net_price: updatedRow.net_price,
                total_net_price: updatedRow.total_net_price,
                discount_price: updatedRow.discount_price,
                total: updatedRow.total,
                volume: updatedRow.volume,
                gross_weight: updatedRow.gross_weight,
                net_weight: updatedRow.net_weight,
                qty_per_packaging: updatedRow.qty_per_packaging,
                length: updatedRow.length,
                width: updatedRow.width,
                height: updatedRow.height,
                radius: updatedRow.radius,
                delivery_date: prepareApiValue("delivery_date", updatedRowDeliveryDatePayload),
            };
            return updatedRow;
        }));

        const stringId = String(rowId);
        if (!resolvedCompanyId || !documentId || stringId.startsWith("new-") || stringId.startsWith("clone-")) {
            return;
        }

        setUpdateError(null);

        const fallbackDeliveryValue = previousSnapshot?.delivery_date
            ?? documentDeliveryDateRawRef.current
            ?? documentDeliveryDateRef.current
            ?? null;
        const fallbackPayload = {
            id: rowId,
            product: {id: product.id},
            delivery_date: prepareApiValue("delivery_date", fallbackDeliveryValue),
        };

        updateDocumentBodyRow({
            companyId: resolvedCompanyId,
            documentId,
            bodyId: rowId,
            data: nextPayload ?? fallbackPayload,
        })
            .then(() => {
                toast.success(t("app.documents.body_product_update_success", {defaultValue: "Prodotto collegato"}));
                notifyDocumentTotalsChange();
            })
            .catch((error) => {
                setUpdateError(error);
                if (previousSnapshot) {
                    setRows((previousRows) => previousRows.map((row) => (
                        row.id === rowId ? previousSnapshot : row
                    )));
                }
            });
    }, [documentId, ensureEditable, notifyDocumentTotalsChange, resolvedCompanyId, t]);

    const handleOpenProductAdvancedSearch = useCallback((rowId) => {
        if (!ensureEditable()) {
            return;
        }
        if (!rowId || !resolvedCompanyId) {
            return;
        }
        setAdvancedSearchRowId(rowId);
        setIsProductAdvancedSearchOpen(true);
    }, [ensureEditable, resolvedCompanyId]);

    const handleToggleAdvancedSearch = useCallback((nextOpen) => {
        if (!nextOpen) {
            setIsProductAdvancedSearchOpen(false);
            setAdvancedSearchRowId(null);
        }
    }, []);

    const handleAdvancedSearchSelect = useCallback(async (product) => {
        if (!ensureEditable()) {
            return;
        }
        if (!advancedSearchRowId || !product?.id) {
            return;
        }
        const resolvedProduct = await resolveProductDetails(product);
        handleAssignProduct(advancedSearchRowId, resolvedProduct, {applyUnitPricing: false});
        setIsProductAdvancedSearchOpen(false);
        setAdvancedSearchRowId(null);
    }, [advancedSearchRowId, ensureEditable, handleAssignProduct, resolveProductDetails]);

    const handleCloseProductEdit = useCallback(() => {
        setIsEditProductOpen(false);
        setEditProductId(null);
        setEditProductRowId(null);
    }, []);

    const handleProductEditRefresh = useCallback((updatedProduct) => {
        if (!editProductRowId) {
            return;
        }
        const normalized = updatedProduct?.product ?? updatedProduct;
        if (!normalized?.id) {
            return;
        }
        handleAssignProduct(editProductRowId, normalized, {
            applyUnitPricing: false,
            preserveExistingPricing: true,
        });
    }, [editProductRowId, handleAssignProduct]);

    const handleSelectDimensions = useCallback((rowId, option) => {
        if (!ensureEditable()) {
            return;
        }
        if (!rowId || !option?.data) {
            return;
        }

        const normalizedPayload = {
            volume: Number(option.data.volume ?? 0) || 0,
            gross_weight: Number(option.data.gross_weight ?? 0) || 0,
            net_weight: Number(option.data.net_weight ?? 0) || 0,
            qty_per_packaging: Number(option.data.qty_per_packaging ?? 0) || 0,
            length: Number(option.data.length ?? 0) || 0,
            width: Number(option.data.width ?? 0) || 0,
            height: Number(option.data.height ?? 0) || 0,
            radius: Number(option.data.radius ?? 0) || 0,
        };

        let previousSnapshot = null;
        setRows((previousRows) => previousRows.map((row) => {
            if (row.id !== rowId) {
                return row;
            }
            previousSnapshot = row;
            return {
                ...row,
                ...normalizedPayload,
            };
        }));

        const stringId = String(rowId);
        if (!resolvedCompanyId || !documentId || stringId.startsWith("new-") || stringId.startsWith("clone-")) {
            return;
        }

        setUpdateError(null);

        const resolvedDeliveryPayload = previousSnapshot?.delivery_date
            ?? documentDeliveryDateRawRef.current
            ?? documentDeliveryDateRef.current
            ?? null;
        const payload = {
            id: rowId,
            ...normalizedPayload,
            delivery_date: prepareApiValue("delivery_date", resolvedDeliveryPayload),
        };

        updateDocumentBodyRow({
            companyId: resolvedCompanyId,
            documentId,
            bodyId: rowId,
            data: payload,
        })
            .then(() => {
                setUpdateError(null);
                toast.success(t("app.documents.body_update_success", {defaultValue: "Riga aggiornata"}));
                notifyDocumentTotalsChange();
            })
            .catch((error) => {
                setUpdateError(error);
                if (previousSnapshot) {
                    setRows((previousRows) => previousRows.map((row) => (
                        row.id === rowId ? previousSnapshot : row
                    )));
                }
            });
    }, [documentId, ensureEditable, notifyDocumentTotalsChange, resolvedCompanyId, t]);

    const handleRemoveConfirmed = useCallback(() => {
        if (!ensureEditable()) {
            deleteResponseRef.current = null;
            return;
        }
        const removedRowId = rowPendingRemove?.id ?? null;
        const removalPayload = deleteResponseRef.current;
        deleteResponseRef.current = null;

        if (removalPayload) {
            const normalized = normalizeBodyRows(removalPayload, document, documentCurrencySymbol);
            const nextRows = normalized.length > 0
                ? normalized
                : deriveRowsFromDocument(document, documentCurrencySymbol);
            setRows(nextRows);
            if (removedRowId) {
                clearSelectionForIds(new Set([removedRowId]));
            }
        } else if (removedRowId) {
            immediateRemoveRow(removedRowId);
        }

        if (removedRowId) {
            toast.success(t("app.documents.body_remove_success", {defaultValue: "Riga eliminata"}));
            notifyDocumentTotalsChange();
        }
        setIsRemoveOpen(false);
        setRowPendingRemove(null);
    }, [clearSelectionForIds, document, documentCurrencySymbol, ensureEditable, immediateRemoveRow, notifyDocumentTotalsChange, rowPendingRemove, t]);

    const handleConfirmReorderModal = useCallback((orderedRows) => {
        if (!ensureEditable()) {
            return;
        }
        if (!Array.isArray(orderedRows) || orderedRows.length === 0) {
            setIsReorderModalOpen(false);
            return;
        }

        const nextRows = orderedRows.map((row, index) => ({
            ...row,
            position: index + 1,
        }));
        const previousSnapshot = rows.map((row) => ({...row}));
        setRows(nextRows);

        if (!resolvedCompanyId || !documentId) {
            setIsReorderModalOpen(false);
            toast.success(t("app.documents.body_reorder_success", {defaultValue: "Riordino aggiornato"}));
            return;
        }

        const persistedIds = nextRows
            .map((row) => row.id)
            .filter((rowId) => rowId && !String(rowId).startsWith("new-") && !String(rowId).startsWith("clone-"));

        if (persistedIds.length === 0) {
            setIsReorderModalOpen(false);
            toast.success(t("app.documents.body_reorder_success", {defaultValue: "Riordino aggiornato"}));
            return;
        }

        setIsReorderSubmitting(true);
        updateDocumentBodyPositions({
            companyId: resolvedCompanyId,
            documentId,
            ids: persistedIds,
        })
            .then((payload) => {
                setUpdateError(null);
                const normalized = normalizeBodyRows(payload, document, documentCurrencySymbol);
                const serverRows = normalized.length > 0
                    ? normalized
                    : deriveRowsFromDocument(document, documentCurrencySymbol);
                const nextServerRows = Array.isArray(serverRows) ? [...serverRows] : [];
                const existingIds = new Set(nextServerRows.map((row) => String(row?.id ?? "")));
                nextRows.forEach((row) => {
                    const rowId = String(row?.id ?? "");
                    if (!rowId || existingIds.has(rowId)) {
                        return;
                    }
                    nextServerRows.push(row);
                    existingIds.add(rowId);
                });
                setRows(nextServerRows);
                toast.success(t("app.documents.body_reorder_success", {defaultValue: "Riordino aggiornato"}));
                setIsReorderModalOpen(false);
            })
            .catch((error) => {
                setUpdateError(error);
                setRows(previousSnapshot);
                toast.error(t("app.documents.body_reorder_error", {defaultValue: "Riordino non riuscito"}));
            })
            .finally(() => {
                setIsReorderSubmitting(false);
            });
    }, [document, documentCurrencySymbol, documentId, ensureEditable, resolvedCompanyId, rows, t]);

    const selectedRowIds = useMemo(() => (
        Object.entries(selectedRows ?? {})
            .filter(([, isChecked]) => Boolean(isChecked))
            .map(([rowId]) => String(rowId))
    ), [selectedRows]);

    const persistedSelectedRowIds = useMemo(() => (
        selectedRowIds.filter((rowId) => (
            rowId
            && !rowId.startsWith("new-")
            && !rowId.startsWith("clone-")
        ))
    ), [selectedRowIds]);

    const hasSelectedRows = selectedRowIds.length > 0;
    const hasPersistedSelection = persistedSelectedRowIds.length > 0;

    const allSelected = useMemo(
        () => rows.length > 0 && rows.every((row) => selectedRows[row.id]),
        [rows, selectedRows]
    );

    const openBatchConfirm = useCallback((action) => {
        if (!ensureEditable()) {
            return;
        }
        if (action === "delete" && !hasSelectedRows) {
            return;
        }
        if (action === "clone" && !hasPersistedSelection) {
            return;
        }
        setBatchAction(action);
        setIsBatchConfirmOpen(true);
    }, [ensureEditable, hasPersistedSelection, hasSelectedRows]);

    const executeBatchDelete = useCallback(async () => {
        if (!ensureEditable()) {
            return;
        }
        if (!hasSelectedRows) {
            return;
        }

        const transientIds = selectedRowIds.filter((rowId) => (
            rowId.startsWith("new-") || rowId.startsWith("clone-")
        ));

        const persistedIds = hasPersistedSelection ? [...persistedSelectedRowIds] : [];

        const removeFromState = (ids) => {
            if (!ids || ids.size === 0) {
                return;
            }
            setRows((previousRows) => previousRows.filter((row) => {
                const rowKey = row?.id ?? null;
                if (rowKey === null || rowKey === undefined) {
                    return true;
                }
                return !ids.has(String(rowKey));
            }));
            clearSelectionForIds(ids);
        };

        if (persistedIds.length === 0) {
            const idsToRemove = new Set(transientIds.map((value) => String(value)));
            removeFromState(idsToRemove);
            if (idsToRemove.size > 0) {
                const transientMessage = idsToRemove.size === 1
                    ? t("app.documents.body_remove_success", {defaultValue: "Riga eliminata"})
                    : t("app.documents.body_remove_success", {defaultValue: "Righe eliminate", count: idsToRemove.size});
                toast.success(transientMessage);
            }
            return;
        }

        if (!resolvedCompanyId || !documentId) {
            toast.error(t("app.common.generic_error", {defaultValue: "Impossibile completare l'eliminazione. Prova di nuovo."}));
            return;
        }

        setBodyError(null);

        try {
            const response = await deleteDocumentBodyRows({companyId: resolvedCompanyId, documentId, ids: persistedIds});
            const normalized = normalizeBodyRows(response, document, documentCurrencySymbol);
            const nextRows = normalized.length > 0
                ? normalized
                : deriveRowsFromDocument(document, documentCurrencySymbol);
            setRows(nextRows);
            const idsToRemove = new Set([...persistedIds, ...transientIds].map((value) => String(value)));
            clearSelectionForIds(idsToRemove);
            const totalRemoved = idsToRemove.size;
            const successMessage = totalRemoved === 1
                ? t("app.documents.body_remove_success", {defaultValue: "Riga eliminata"})
                : t("app.documents.body_remove_success", {defaultValue: "Righe eliminate", count: totalRemoved});
            toast.success(successMessage);
            notifyDocumentTotalsChange();
        } catch (error) {
            setBodyError(error);
            toast.error(t("app.common.generic_error", {defaultValue: "Impossibile completare l'eliminazione. Prova di nuovo."}));
        }
    }, [clearSelectionForIds, document, documentCurrencySymbol, documentId, ensureEditable, hasPersistedSelection, hasSelectedRows, notifyDocumentTotalsChange, persistedSelectedRowIds, resolvedCompanyId, selectedRowIds, t]);

    const executeBatchClone = useCallback(async () => {
        if (!canCloneDocument) {
            toast.error(t("app.common.permission_denied", {defaultValue: "Operazione non consentita"}));
            return;
        }
        if (!ensureEditable()) {
            return;
        }
        if (!hasPersistedSelection || !resolvedCompanyId || !documentId) {
            return;
        }

        setBodyError(null);

        try {
            await cloneDocumentBodyRows({companyId: resolvedCompanyId, documentId, ids: persistedSelectedRowIds});
            const response = await fetchDocumentBody({companyId: resolvedCompanyId, documentId, params: bodyFetchParams});
            const normalized = normalizeBodyRows(response, document, documentCurrencySymbol);
            const nextRows = normalized.length > 0
                ? normalized
                : deriveRowsFromDocument(document, documentCurrencySymbol);
            setRows(nextRows);
            setSelectedRows({});
            pendingScrollRowIdRef.current = nextRows.length > 0
                ? nextRows[nextRows.length - 1]?.id ?? null
                : null;
            shouldScrollRef.current = true;
            const successMessage = persistedSelectedRowIds.length === 1
                ? t("app.documents.body_clone_success", {defaultValue: "Riga duplicata"})
                : t("app.documents.body_clone_success", {defaultValue: "Righe duplicate", count: persistedSelectedRowIds.length});
            toast.success(successMessage);
            notifyDocumentTotalsChange();
        } catch (error) {
            setBodyError(error);
            toast.error(t("app.common.generic_error", {defaultValue: "Impossibile completare la duplicazione. Prova di nuovo."}));
        }
    }, [bodyFetchParams, canCloneDocument, document, documentCurrencySymbol, documentId, ensureEditable, hasPersistedSelection, notifyDocumentTotalsChange, persistedSelectedRowIds, resolvedCompanyId, t]);

    const executeBatchHide = useCallback(async () => {
        if (!canHideDocumentRows) {
            toast.error(t("app.common.permission_denied", {defaultValue: "Operazione non consentita"}));
            return;
        }
        if (!ensureEditable()) {
            return;
        }
        if (!hasPersistedSelection || !resolvedCompanyId || !documentId) {
            return;
        }

        setBodyError(null);
        setIsVisibilityUpdating(true);

        try {
            const response = await hideDocumentBodyRows({companyId: resolvedCompanyId, documentId, ids: persistedSelectedRowIds});
            const normalized = normalizeBodyRows(response, document, documentCurrencySymbol);
            const nextRows = normalized.length > 0
                ? normalized
                : deriveRowsFromDocument(document, documentCurrencySymbol);
            setRows(nextRows);
            setSelectedRows({});
            const hiddenCount = persistedSelectedRowIds.length;
            const successMessage = hiddenCount === 1
                ? t("app.documents.body_hide_success", {defaultValue: "Riga nascosta"})
                : t("app.documents.body_hide_success", {defaultValue: "Righe nascoste", count: hiddenCount});
            toast.success(successMessage);
            notifyDocumentTotalsChange();
        } catch (error) {
            setBodyError(error);
            toast.error(t("app.documents.body_visibility_error", {defaultValue: "Impossibile aggiornare la visibilità delle righe. Riprova."}));
        } finally {
            setIsVisibilityUpdating(false);
        }
    }, [canHideDocumentRows, document, documentCurrencySymbol, documentId, ensureEditable, hasPersistedSelection, notifyDocumentTotalsChange, persistedSelectedRowIds, resolvedCompanyId, t]);

    const executeBatchUnhide = useCallback(async () => {
        if (!canHideDocumentRows) {
            toast.error(t("app.common.permission_denied", {defaultValue: "Operazione non consentita"}));
            return;
        }
        if (!ensureEditable()) {
            return;
        }
        if (!hasPersistedSelection || !resolvedCompanyId || !documentId) {
            return;
        }

        setBodyError(null);
        setIsVisibilityUpdating(true);

        try {
            const response = await unhideDocumentBodyRows({companyId: resolvedCompanyId, documentId, ids: persistedSelectedRowIds});
            const normalized = normalizeBodyRows(response, document, documentCurrencySymbol);
            const nextRows = normalized.length > 0
                ? normalized
                : deriveRowsFromDocument(document, documentCurrencySymbol);
            setRows(nextRows);
            setSelectedRows({});
            const visibleCount = persistedSelectedRowIds.length;
            const successMessage = visibleCount === 1
                ? t("app.documents.body_unhide_success", {defaultValue: "Riga visualizzata"})
                : t("app.documents.body_unhide_success", {defaultValue: "Righe visualizzate", count: visibleCount});
            toast.success(successMessage);
            notifyDocumentTotalsChange();
        } catch (error) {
            setBodyError(error);
            toast.error(t("app.documents.body_visibility_error", {defaultValue: "Impossibile aggiornare la visibilità delle righe. Riprova."}));
        } finally {
            setIsVisibilityUpdating(false);
        }
    }, [canHideDocumentRows, document, documentCurrencySymbol, documentId, ensureEditable, hasPersistedSelection, notifyDocumentTotalsChange, persistedSelectedRowIds, resolvedCompanyId, t]);

    const handleBatchConfirm = useCallback(async () => {
        if (batchAction === "delete") {
            await executeBatchDelete();
        } else if (batchAction === "clone") {
            await executeBatchClone();
        }
        setBatchAction(null);
    }, [batchAction, executeBatchClone, executeBatchDelete]);

    const batchConfirmDetail = useMemo(() => {
        if (!batchAction) {
            return null;
        }

        if (batchAction === "delete") {
            const totalCount = selectedRowIds.length;
            const persistedCount = persistedSelectedRowIds.length;
            const transientCount = totalCount - persistedCount;
            return {
                title: t("app.documents.body_batch_remove_title", {defaultValue: "Elimina righe selezionate"}),
                message: transientCount > 0
                    ? t(
                        "app.documents.body_batch_remove_description",
                        {
                            defaultValue: `Vuoi eliminare ${totalCount} righe? ${transientCount === 1
                                ? "La riga non salvata verrà rimossa solo localmente."
                                : `${transientCount} righe non salvate verranno rimosse solo localmente.`}`,
                            count: totalCount,
                            transient: transientCount,
                        }
                    )
                    : t("app.documents.body_batch_remove_description", {
                        defaultValue: `Vuoi eliminare ${totalCount} righe selezionate?`,
                        count: totalCount,
                    }),
            };
        }

        if (batchAction === "clone") {
            const persistedCount = persistedSelectedRowIds.length;
            const excluded = selectedRowIds.length - persistedCount;
            return {
                title: t("app.documents.body_batch_clone_title", {defaultValue: "Duplica righe selezionate"}),
                message: excluded > 0
                    ? t(
                        "app.documents.body_batch_clone_description",
                        {
                            defaultValue: `Solo ${persistedCount} righe salvate verranno duplicate. ${excluded === 1
                                ? "Una riga non salvata sarà ignorata."
                                : `${excluded} righe non salvate saranno ignorate.`} Procedere?`,
                            count: persistedCount,
                            excluded,
                        }
                    )
                    : t("app.documents.body_batch_clone_description", {
                        defaultValue: `Vuoi duplicare ${persistedCount} righe selezionate?`,
                        count: persistedCount,
                    }),
            };
        }

        return null;
    }, [batchAction, persistedSelectedRowIds.length, selectedRowIds.length, t]);

    const handleBatchConfirmToggle = useCallback((nextOpen) => {
        setIsBatchConfirmOpen(Boolean(nextOpen));
        if (!nextOpen) {
            setBatchAction(null);
        }
    }, []);

    const handleOpenBulkPurchasePriceModal = useCallback(() => {
        if (!ensureEditable()) {
            return;
        }
        if (!hasSelectedRows) {
            return;
        }
        setBulkPurchasePriceScope(BULK_PURCHASE_PRICE_SCOPE.FILL_MISSING);
        setIsBulkPurchasePriceModalOpen(true);
    }, [ensureEditable, hasSelectedRows]);

    const handleCloseBulkPurchasePriceModal = useCallback(() => {
        if (isBulkPurchasePriceUpdating) {
            return;
        }
        setIsBulkPurchasePriceModalOpen(false);
    }, [isBulkPurchasePriceUpdating]);

    const handleConfirmBulkPurchasePrice = useCallback(async () => {
        if (!ensureEditable()) {
            return;
        }
        if (!hasSelectedRows) {
            return;
        }

        const persistedIds = [...persistedSelectedRowIds];
        if (persistedIds.length === 0) {
            toast(t("app.documents.body_mass_actions_purchase_price_empty", {defaultValue: "Nessuna riga salvata da aggiornare"}));
            return;
        }

        if (!resolvedCompanyId || !documentId || !resolvedDocumentType) {
            toast.error(t("app.documents.body_mass_actions_purchase_price_error", {defaultValue: "Impossibile aggiornare i prezzi di acquisto. Riprova."}));
            return;
        }

        setIsBulkPurchasePriceUpdating(true);
        setBodyError(null);

        const shouldOverwriteExisting = bulkPurchasePriceScope === BULK_PURCHASE_PRICE_SCOPE.OVERWRITE_ALL;
        const payload = {
            ids: persistedIds,
            mode: shouldOverwriteExisting
                ? BULK_PURCHASE_PRICE_SCOPE.OVERWRITE_ALL
                : BULK_PURCHASE_PRICE_SCOPE.FILL_MISSING,
        };

        try {
            await updateDocumentBodyPurchasePrices({
                companyId: resolvedCompanyId,
                type: resolvedDocumentType,
                documentId,
                data: payload,
            });

            try {
                const refreshed = await fetchDocumentBody({companyId: resolvedCompanyId, documentId, params: bodyFetchParams});
                const normalized = normalizeBodyRows(refreshed, document, documentCurrencySymbol);
                const nextRows = normalized.length > 0
                    ? normalized
                    : deriveRowsFromDocument(document, documentCurrencySymbol);
                setRows(nextRows);
                setSelectedRows({});
            } catch (refreshError) {
                if (refreshError?.name !== "AbortError") {
                    setBodyError(refreshError);
                }
            }

            const successKey = persistedIds.length === 1
                ? "app.documents.body_mass_actions_purchase_price_success"
                : "app.documents.body_mass_actions_purchase_price_success_plural";
            toast.success(t(successKey, {
                defaultValue: persistedIds.length === 1
                    ? "Prezzo di acquisto aggiornato"
                    : "Prezzi di acquisto aggiornati",
                count: persistedIds.length,
            }));
            notifyDocumentTotalsChange();
            setIsBulkPurchasePriceModalOpen(false);
        } catch (error) {
            setBodyError(error);
            toast.error(t("app.documents.body_mass_actions_purchase_price_error", {defaultValue: "Impossibile aggiornare i prezzi di acquisto. Riprova."}));
            return;
        } finally {
            setIsBulkPurchasePriceUpdating(false);
        }
    }, [
        bulkPurchasePriceScope,
        bodyFetchParams,
        documentId,
        document,
        documentCurrencySymbol,
        ensureEditable,
        hasSelectedRows,
        notifyDocumentTotalsChange,
        persistedSelectedRowIds,
        resolvedCompanyId,
        resolvedDocumentType,
        t,
    ]);

    const handleOpenBulkConfirmQuantity = useCallback(() => {
        if (!ensureEditable()) {
            return;
        }
        if (!hasSelectedRows) {
            return;
        }
        if (!canManageDocumentConfirmedQuantity) {
            toast.error(t("app.common.permission_denied", {defaultValue: "Operazione non consentita"}));
            return;
        }
        if (!hasPersistedSelection) {
            toast(t("app.documents.body_mass_actions_confirmed_quantity_empty", {defaultValue: "Nessuna riga salvata da aggiornare"}));
            return;
        }
        setIsBulkConfirmQuantityConfirmOpen(true);
    }, [canManageDocumentConfirmedQuantity, ensureEditable, hasPersistedSelection, hasSelectedRows, t]);

    const handleConfirmBulkConfirmQuantity = useCallback(async () => {
        if (!ensureEditable()) {
            return;
        }
        if (!canManageDocumentConfirmedQuantity) {
            toast.error(t("app.common.permission_denied", {defaultValue: "Operazione non consentita"}));
            return;
        }

        const persistedIds = [...persistedSelectedRowIds];
        if (persistedIds.length === 0) {
            toast(t("app.documents.body_mass_actions_confirmed_quantity_empty", {defaultValue: "Nessuna riga salvata da aggiornare"}));
            return;
        }

        if (!resolvedCompanyId || !documentId) {
            toast.error(t("app.documents.body_mass_actions_confirmed_quantity_error", {defaultValue: "Impossibile aggiornare le quantità confermate. Riprova."}));
            return;
        }

        setIsBulkConfirmQuantityUpdating(true);
        setBodyError(null);

        try {
            await confirmDocumentBodyRows({
                companyId: resolvedCompanyId,
                documentId,
                ids: persistedIds,
            });

            try {
                const refreshed = await fetchDocumentBody({companyId: resolvedCompanyId, documentId, params: bodyFetchParams});
                const normalized = normalizeBodyRows(refreshed, document, documentCurrencySymbol);
                const nextRows = normalized.length > 0
                    ? normalized
                    : deriveRowsFromDocument(document, documentCurrencySymbol);
                setRows(nextRows);
                setSelectedRows({});
            } catch (refreshError) {
                if (refreshError?.name !== "AbortError") {
                    setBodyError(refreshError);
                }
            }

            const successKey = persistedIds.length === 1
                ? "app.documents.body_mass_actions_confirmed_quantity_success"
                : "app.documents.body_mass_actions_confirmed_quantity_success_plural";
            toast.success(t(successKey, {
                defaultValue: persistedIds.length === 1
                    ? "Quantità confermata aggiornata"
                    : "Quantità confermate aggiornate",
                count: persistedIds.length,
            }));
            notifyDocumentTotalsChange();
        } catch (error) {
            setBodyError(error);
            toast.error(t("app.documents.body_mass_actions_confirmed_quantity_error", {defaultValue: "Impossibile aggiornare le quantità confermate. Riprova."}));
        } finally {
            setIsBulkConfirmQuantityUpdating(false);
        }
    }, [
        bodyFetchParams,
        canManageDocumentConfirmedQuantity,
        document,
        documentCurrencySymbol,
        documentId,
        ensureEditable,
        notifyDocumentTotalsChange,
        persistedSelectedRowIds,
        resolvedCompanyId,
        t,
    ]);

    const massActionButtons = useMemo(() => {
        if (!isEditable || !hasSelectedRows) {
            return null;
        }

        const deleteLabel = t("app.common.delete", {defaultValue: "Elimina"});
        const cloneLabel = t("app.common.clone", {defaultValue: "Duplica"});
        const hideLabel = t("app.documents.body_hide_rows", {defaultValue: "Nascondi righe"});
        const showLabel = t("app.documents.body_unhide_rows", {defaultValue: "Visualizza righe"});
        const massActionsLabel = t("app.documents.body_mass_actions_title", {defaultValue: "Azioni massive"});
        const purchasePriceLabel = t("app.documents.body_mass_actions_purchase_price", {defaultValue: "Ricalcola prezzo di acquisto"});
        const purchasePriceDescription = t("app.documents.body_mass_actions_purchase_price_description", {defaultValue: "Aggiorna rapidamente il prezzo di acquisto partendo dai dati del prodotto selezionato."});
        const confirmQuantityLabel = t("app.documents.body_mass_actions_confirmed_quantity", {defaultValue: "Quantità confermate"});
        const confirmQuantityDescription = t("app.documents.body_mass_actions_confirmed_quantity_description", {defaultValue: "Gestisci le quantità confermate per questa riga di documento."});
        const visibilityDisabled = !hasPersistedSelection || isVisibilityUpdating;
        const confirmQuantityActionDisabled = isBulkConfirmQuantityUpdating || !canManageDocumentConfirmedQuantity;
        const isMassActionProcessing = isBulkPurchasePriceUpdating || isBulkConfirmQuantityUpdating;
        const visibilityButtonClass = visibilityDisabled
            ? "inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-gray-100 text-gray-300 cursor-not-allowed"
            : "inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-100";
        const massActionButtonClass = isMassActionProcessing
            ? "inline-flex h-8 items-center gap-2 rounded-md border border-gray-200 bg-gray-100 px-3 py-2 text-sm font-medium text-gray-400 cursor-not-allowed"
            : "inline-flex h-8 items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100";

        const actionButtons = [
            (
                <Menu as="div" className="relative" key="mass-actions-menu">
                    <Menu.Button
                        className={`${massActionButtonClass} h-[32px]`}
                        disabled={isMassActionProcessing}
                    >
                        <AdjustmentsIcon className="h-4 w-4" />
                        <span>{massActionsLabel}</span>
                        <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                    </Menu.Button>
                    <Menu.Items className="absolute left-0 z-30 mt-2 w-72 origin-top-left rounded-lg border border-gray-100 bg-white p-1 shadow-2xl focus:outline-none">
                        <Menu.Item>
                            {({active}) => (
                                <button
                                    type="button"
                                    onClick={handleOpenBulkPurchasePriceModal}
                                    className={`flex w-full flex-col gap-1 rounded-md px-3 py-2 text-left text-sm ${active ? "bg-blue-50 text-blue-900" : "text-gray-700"}`}
                                >
                                    <span className="font-semibold leading-tight">{purchasePriceLabel}</span>
                                    <span className="text-xs text-gray-500">{purchasePriceDescription}</span>
                                </button>
                            )}
                        </Menu.Item>
                        {canManageDocumentConfirmedQuantity && (
                            <Menu.Item>
                                {({active}) => (
                                    <button
                                        type="button"
                                        onClick={handleOpenBulkConfirmQuantity}
                                        disabled={confirmQuantityActionDisabled}
                                        className={`flex w-full flex-col gap-1 rounded-md px-3 py-2 text-left text-sm ${
                                            confirmQuantityActionDisabled
                                                ? "cursor-not-allowed text-gray-300"
                                                : active
                                                    ? "bg-blue-50 text-blue-900"
                                                    : "text-gray-700"
                                        }`}
                                    >
                                        <span className="font-semibold leading-tight">{confirmQuantityLabel}</span>
                                        <span className="text-xs text-gray-500">{confirmQuantityDescription}</span>
                                    </button>
                                )}
                            </Menu.Item>
                        )}
                    </Menu.Items>
                </Menu>
            ),
        ];

        actionButtons.push(
            <button
                key="delete"
                type="button"
                onClick={() => openBatchConfirm("delete")}
                className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 hover:bg-red-50 hover:text-red-600 h-[32px]"
            >
                <TrashIcon className="h-4 w-4" />
                <span>{deleteLabel}</span>
            </button>
        );

        if (canCloneDocument) {
            actionButtons.push(
                <button
                    key="clone"
                    type="button"
                    onClick={() => openBatchConfirm("clone")}
                    disabled={!hasPersistedSelection}
                    className={hasPersistedSelection
                        ? "inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 h-[32px]"
                        : "inline-flex items-center gap-2 rounded-md border border-gray-200 bg-gray-100 px-3 py-2 text-sm font-medium text-gray-300 cursor-not-allowed h-[32px]"
                    }
                    title={!hasPersistedSelection
                        ? t("app.documents.body_batch_clone_disabled", {defaultValue: "Seleziona almeno una riga salvata per duplicare."})
                        : undefined}
                >
                    <CloneIcon className="h-4 w-4" />
                    <span>{cloneLabel}</span>
                </button>
            );
        }

        if (canHideDocumentRows) {
            actionButtons.push(
                <button
                    key="hide"
                    type="button"
                    onClick={executeBatchHide}
                    disabled={visibilityDisabled}
                    className={visibilityButtonClass}
                    title={hideLabel}
                    aria-label={hideLabel}
                >
                    <EyeOffIcon className="h-4 w-4" />
                </button>,
                <button
                    key="show"
                    type="button"
                    onClick={executeBatchUnhide}
                    disabled={visibilityDisabled}
                    className={visibilityButtonClass}
                    title={showLabel}
                    aria-label={showLabel}
                >
                    <EyeIcon className="h-4 w-4" />
                </button>,
            );
        }

        if (actionButtons.length === 0) {
            return null;
        }

        return (
            <div className="flex items-center gap-2">
                {actionButtons}
            </div>
        );
    }, [
        canCloneDocument,
        canHideDocumentRows,
        executeBatchHide,
        executeBatchUnhide,
        handleOpenBulkPurchasePriceModal,
        hasPersistedSelection,
        hasSelectedRows,
        isBulkPurchasePriceUpdating,
        isEditable,
        isVisibilityUpdating,
        openBatchConfirm,
        canManageDocumentConfirmedQuantity,
        handleOpenBulkConfirmQuantity,
        isBulkConfirmQuantityUpdating,
        t,
    ]);

    const bulkConfirmQuantityConfirmDetail = useMemo(() => ({
        title: t("app.documents.body_mass_actions_confirmed_quantity", {defaultValue: "Quantità confermate"}),
        message: t("app.documents.body_mass_actions_confirmed_quantity_confirm", {
            defaultValue: `Confermare le quantità per ${persistedSelectedRowIds.length} righe selezionate?`,
            count: persistedSelectedRowIds.length,
        }),
    }), [persistedSelectedRowIds.length, t]);

    const tableListKey = "document-body";

    const initialColumnVisibility = useMemo(() => ({
        dimensions: false,
        volume: false,
        gross_weight: false,
        net_weight: false,
        qty_per_packaging: false,
        length: false,
        width: false,
        height: false,
        radius: false,
        note: false,
    }), []);

    const selectionColumn = useMemo(() => {
        if (!isEditable) {
            return null;
        }
        return {
            id: "select",
            header: () => (
                <div className={"flex justify-end"}>
                    <input
                        type="checkbox"
                        aria-label={t("app.common.select_all", {defaultValue: "Select all"})}
                        checked={allSelected}
                        onChange={isEditable ? handleToggleAllRows : undefined}
                        disabled={!isEditable}
                        className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ${isEditable ? "" : "cursor-not-allowed opacity-60"}`}
                    />
                </div>
            ),
            cell: ({row}) => (
                <div className={"flex justify-end"}>

                <input
                    type="checkbox"
                    aria-label={t("app.common.select", {defaultValue: "Select"})}
                    checked={Boolean(selectedRows[row.original.id])}
                    onChange={isEditable ? () => handleToggleRow(row.original.id) : undefined}
                    disabled={!isEditable}
                    className={`h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 ${isEditable ? "" : "cursor-not-allowed opacity-60"}`}
                />
                </div>
            ),
            size: 50,
            minSize: 50,
            maxSize: 50,
            enableSorting: false,
            enableHiding: false,
            enableResizing: false,
            meta: isMobileViewport
                ? {dragHandle: false, disableColumnReorder: true}
                : {pin: "left", dragHandle: false, disableColumnReorder: true},
        };
    }, [allSelected, handleToggleAllRows, handleToggleRow, isEditable, isMobileViewport, selectedRows, t]);

    const dataColumns = useMemo(() => [
        {
            accessorKey: "position",
            header: t("app.documents.body_row_number", {defaultValue: "N. riga"}),
            size: 50,
            minSize: 50,
            maxSize: 50,
            // left: isMobileViewport ? undefined :  (isEditable ? 88 : 0),
            enableSorting: false,
            enableHiding: false,
            enableResizing: false,
            meta: isMobileViewport ? undefined : {pin: "left"},
            cell: ({getValue}) => (
                <span className="text-sm text-gray-900">
                    {getValue() ?? ""}
                </span>
            ),
        },
        {
            accessorKey: "name",
            header: t("app.documents.product_name", {defaultValue: "Prodotto"}),
            size: 250,
            minSize: 180,
            // left: isMobileViewport ? undefined : ( isEditable ? 156 : 63),
            enableResizing: true,
            meta: isMobileViewport ? undefined : {pin: "left", allowPinnedResize: true},
            cell: ({row, column, getValue}) => {
                const label = getValue() ?? "";

                return (
                    <DocumentBodyProductLookupCell
                        value={label}
                        onCommit={(nextValue) => handleCellChange(row.original.id, column.id, nextValue)}
                        onSelectProduct={(product) => handleAssignProduct(row.original.id, product, {applyUnitPricing: false})}
                        currentPicture={row.original.picture}
                        companyId={resolvedCompanyId}
                        t={t}
                        onCreateProduct={canCreateProduct ? () => handleOpenProductCreate(row.original.id) : undefined}
                        onEditProduct={canEditProduct ? () => handleOpenProductEdit(row.original.id, row.original.product) : undefined}
                        onOpenAdvancedSearch={() => handleOpenProductAdvancedSearch(row.original.id)}
                        product={row.original.product}
                        isEditable={isEditable}
                    />
                );
            },
        },
        {
            accessorKey: "product_reference",
            header: t("app.documents.product_code", {defaultValue: "Codice prodotto"}),
            cell: ({getValue}) => {
                const value = getValue();
                return (
                    <span className="text-sm text-gray-900">{value ?? ""}</span>
                );
            },
        },
        {
            id: "manufacture_reference",
            accessorFn: (row) => row?.product?.manufacture_reference ?? row?.manufacture_reference ?? "",
            header: t("app.documents.manufacture_reference", {defaultValue: "Manufacture reference"}),
            cell: ({getValue}) => {
                const value = getValue();
                return (
                    <span className="text-sm text-gray-900">{value || "-"}</span>
                );
            },
        },
        {
            accessorKey: "delivery_date",
            header: t("app.documents.delivery_date", {defaultValue: "Data consegna"}),
            cell: ({row, column, getValue}) => (
                <DocumentBodyEditableDateInput
                    value={getValue() ?? ""}
                    onCommit={(nextValue) => handleCellChange(row.original.id, column.id, nextValue)}
                    isEditable={isEditable}
                />
            ),
        },
        {
            accessorKey: "note",
            header: t("app.documents.internal_note", {defaultValue: "Nota interna"}),
            cell: ({row, column, getValue}) => (
                <DocumentBodyEditableTextarea
                    value={getValue() ?? ""}
                    onCommit={(nextValue) => handleCellChange(row.original.id, column.id, nextValue)}
                    placeholder={t("app.documents.internal_note_placeholder", {defaultValue: "Aggiungi nota interna"})}
                    isEditable={isEditable}
                />
            ),
        },
        {
            accessorKey: "dimensions",
            header: t("app.products.package_title", {defaultValue: "Confezione"}),
            cell: ({row}) => {
                const product = row.original.product;
                const dimensions = Array.isArray(product?.product_dimensions) ? product.product_dimensions : [];
                if (!product || dimensions.length === 0) {
                    return null;
                }
                return (
                    <DocumentBodyDimensionsCell
                        dimensions={dimensions}
                        metrics={{
                            volume: row.original.volume,
                            gross_weight: row.original.gross_weight,
                            net_weight: row.original.net_weight,
                            qty_per_packaging: row.original.qty_per_packaging,
                            length: row.original.length,
                            width: row.original.width,
                            height: row.original.height,
                            radius: row.original.radius,
                        }}
                        selectLabel={t("app.products.package_title", {defaultValue: "Confezione"})}
                        onSelect={(option) => handleSelectDimensions(row.original.id, option)}
                        isEditable={isEditable}
                    />
                );
            },
        },
        {
            accessorKey: "volume",
            header: t("app.documents.volume", {defaultValue: "Volume"}),
            cell: ({row, column, getValue}) => (
                <DocumentBodyEditableFormattedNumberInput
                    value={getValue() ?? ""}
                    onCommit={(nextValue) => handleCellChange(row.original.id, column.id, nextValue)}
                    decimalScale={3}
                    isEditable={isEditable}
                />
            ),
        },
        {
            accessorKey: "gross_weight",
            header: t("app.documents.gross_weight", {defaultValue: "Peso lordo"}),
            cell: ({row, column, getValue}) => (
                <DocumentBodyEditableFormattedNumberInput
                    value={getValue() ?? ""}
                    onCommit={(nextValue) => handleCellChange(row.original.id, column.id, nextValue)}
                    decimalScale={3}
                    isEditable={isEditable}
                />
            ),
        },
        {
            accessorKey: "net_weight",
            header: t("app.documents.net_weight", {defaultValue: "Peso netto"}),
            cell: ({row, column, getValue}) => (
                <DocumentBodyEditableFormattedNumberInput
                    value={getValue() ?? ""}
                    onCommit={(nextValue) => handleCellChange(row.original.id, column.id, nextValue)}
                    decimalScale={3}
                    isEditable={isEditable}
                />
            ),
        },
        {
            accessorKey: "qty_per_packaging",
            header: t("app.documents.qty_per_packaging", {defaultValue: "Q.tà per collo"}),
            cell: ({row, column, getValue}) => (
                <DocumentBodyEditableFormattedNumberInput
                    value={getValue() ?? ""}
                    onCommit={(nextValue) => handleCellChange(row.original.id, column.id, nextValue)}
                    decimalScale={2}
                    isEditable={isEditable}
                />
            ),
        },
        {
            accessorKey: "length",
            header: t("app.documents.length", {defaultValue: "Lunghezza"}),
            cell: ({row, column, getValue}) => (
                <DocumentBodyEditableFormattedNumberInput
                    value={getValue() ?? ""}
                    onCommit={(nextValue) => handleCellChange(row.original.id, column.id, nextValue)}
                    decimalScale={2}
                    isEditable={isEditable}
                />
            ),
        },
        {
            accessorKey: "width",
            header: t("app.documents.width", {defaultValue: "Larghezza"}),
            cell: ({row, column, getValue}) => (
                <DocumentBodyEditableFormattedNumberInput
                    value={getValue() ?? ""}
                    onCommit={(nextValue) => handleCellChange(row.original.id, column.id, nextValue)}
                    decimalScale={2}
                    isEditable={isEditable}
                />
            ),
        },
        {
            accessorKey: "height",
            header: t("app.documents.height", {defaultValue: "Altezza"}),
            cell: ({row, column, getValue}) => (
                <DocumentBodyEditableFormattedNumberInput
                    value={getValue() ?? ""}
                    onCommit={(nextValue) => handleCellChange(row.original.id, column.id, nextValue)}
                    decimalScale={2}
                    isEditable={isEditable}
                />
            ),
        },
        {
            accessorKey: "radius",
            header: t("app.documents.radius", {defaultValue: "Raggio"}),
            cell: ({row, column, getValue}) => (
                <DocumentBodyEditableFormattedNumberInput
                    value={getValue() ?? ""}
                    onCommit={(nextValue) => handleCellChange(row.original.id, column.id, nextValue)}
                    decimalScale={2}
                    isEditable={isEditable}
                />
            ),
        },
        {
            accessorKey: "quantity",
            header: t("app.documents.quantity", {defaultValue: "Quantità"}),
            cell: ({row, column, getValue}) => {
                const rowId = row.original?.id;
                const rowKey = rowId !== undefined && rowId !== null ? String(rowId) : "";
                const isPersisted = Boolean(rowKey) && !rowKey.startsWith("new-") && !rowKey.startsWith("clone-");
                const managedEvents = confirmedQuantityEvents[rowKey];
                let confirmedQuantity = resolveQuantityFromManagedEvents(managedEvents);

                if (confirmedQuantity === 0) {
                    confirmedQuantity = resolveConfirmedQuantityFromRow(row.original);
                }
                const quantityValue = numericOrNull(row.original?.quantity) ?? numericOrNull(getValue());
                const badgeValue = formatConfirmedQuantityBadge(confirmedQuantity, quantityValue);
                const hasEvents = badgeValue !== "";
                const canManageConfirmedQuantity = isEditable && isPersisted && canManageDocumentConfirmedQuantity;
                const buttonTitle = !isEditable
                    ? t("app.documents.confirmed_quantity_readonly", {defaultValue: "Documento confermato: modifica non consentita"})
                    : (isPersisted && canManageDocumentConfirmedQuantity)
                        ? t("app.documents.confirmed_quantity_manage", {defaultValue: "Gestisci quantità confermate"})
                        : t("app.documents.confirmed_quantity_unavailable_for_draft", {defaultValue: "Salva la riga prima di gestire le quantità confermate"});
                const packagingSize = resolveRowPackagingQuantity(row.original);
                const canShowPackagingWarning = Boolean(isExporter ?? true);

                const hasQuantityWarning = canShowPackagingWarning
                    && hasPackagingQuantityMismatch(row.original?.quantity, packagingSize);


                const packagingWarningDetails = hasQuantityWarning
                    ? resolvePackagingWarningDetails(row.original?.quantity, packagingSize, t)
                    : null;
                const showPackagingWarning = Boolean(packagingWarningDetails);
                const warningTitle = packagingWarningDetails?.title ?? "";
                const warningDescription = packagingWarningDetails?.description ?? "";
                const warningLabel = showPackagingWarning
                    ? warningDescription
                        ? `${warningTitle}. ${warningDescription}`
                        : warningTitle
                    : "";

                return (
                    <div className="flex items-center gap-2">
                        <div className="flex-1">
                            <DocumentBodyEditableFormattedNumberInput
                                value={getValue() ?? ""}
                                onCommit={(nextValue) => handleCellChange(row.original.id, column.id, nextValue)}
                                decimalScale={2}
                                isEditable={isEditable}
                            />
                        </div>
                        {showPackagingWarning ? (
                            <Popover className="relative">
                                <Popover.Button
                                    type="button"
                                    className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-red-50 text-red-600 shadow-sm transition hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40"
                                    title={warningLabel || warningTitle}
                                    aria-label={warningLabel || warningTitle}
                                >
                                    <ExclamationCircleIcon className="h-4 w-4" aria-hidden="true" />
                                </Popover.Button>
                                <Popover.Panel className="absolute right-0 z-10 mt-2 min-w-[14rem] max-w-xs rounded-md border border-red-100 bg-white p-3 text-xs text-gray-700 shadow-lg ring-1 ring-black/5">
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-red-600">{warningTitle}</p>
                                    {warningDescription ? (
                                        <p className="mt-1 leading-snug">{warningDescription}</p>
                                    ) : null}
                                </Popover.Panel>
                            </Popover>
                        ) : null}
                        {isEditable || hasEvents ? (
                            <button
                                type="button"
                                onClick={() => handleOpenConfirmedQuantity(rowId)}
                                disabled={!canManageConfirmedQuantity}
                                title={buttonTitle}
                                aria-label={buttonTitle}
                                className={canManageConfirmedQuantity
                                    ? "relative inline-flex items-center justify-center  text-gray-600"
                                    : "relative inline-flex items-center justify-center  text-gray-300 cursor-not-allowed"}
                            >
                                <span className="pointer-events-none relative inline-flex">
                                    <BoxIcon className={hasEvents ? "h-5 w-5 text-green-600" : "h-5 w-5"} aria-hidden="true" />
                                    {hasEvents && (
                                        <span className="absolute -top-1.5 -right-2 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-green-600 px-1 text-[10px] font-semibold text-white">
                                            {badgeValue}
                                        </span>
                                    )}
                                </span>
                            </button>
                        ) : null}
                    </div>
                );
            },
        },
        {
            accessorKey: "purchase_price",
            header: t("app.documents.purchase_price", {defaultValue: "Prezzo acquisto"}),
            cell: ({row, column, getValue}) => {
                const symbol = row.original.currency_symbol ?? documentCurrencySymbol;
                const canViewHistory = canViewPurchaseHistory && Boolean(row.original.product?.id);
                return (
                    <DocumentBodyPurchasePriceCell
                        value={getValue() ?? ""}
                        symbol={symbol}
                        t={t}
                        isEditable={isEditable}
                        onCommit={(nextValue) => handleCellChange(
                            row.original.id,
                            column.id,
                            nextValue,
                            {forceListPricePayload: true}
                        )}
                        onOpenHistory={() => handleOpenPurchaseHistory(row.original.id)}
                        canViewHistory={canViewHistory}
                    />
                );
            },
        },
        {
            accessorKey: "unit_price",
            header: t("app.documents.unit_price", {defaultValue: "Prezzo unitario"}),
            cell: ({row, column, getValue}) => {
                const symbol = row.original.currency_symbol ?? documentCurrencySymbol;
                return (
                    <DocumentBodyUnitPriceCell
                        value={getValue() ?? ""}
                        onCommit={(nextValue) => handleCellChange(row.original.id, column.id, nextValue)}
                        symbol={symbol}
                        isEditable={isEditable}
                    />
                );
            },
        },
        {
            accessorKey: "unit_price_discounts",
            header: t("app.documents.unit_price_discounts"),
            cell: ({row, column, getValue}) => {
                const symbol = row.original.currency_symbol ?? documentCurrencySymbol;
                return (
                    <DocumentBodyEditableDiscountListInput
                        value={getValue()}
                        onCommit={(nextValue) => handleCellChange(row.original.id, column.id, nextValue)}
                        suffix={symbol ? ` ${symbol}` : ""}
                        t={t}
                        isEditable={isEditable}
                    />
                );
            },
        },
        {
            accessorKey: "net_price",
            header: t("app.documents.net_price", {defaultValue: "Prezzo netto"}),
            cell: ({row, getValue}) => {
                const symbol = row.original.currency_symbol ?? documentCurrencySymbol;
                const prices = row.original.last_prices ?? row.original.product?.last_prices ?? null;
                return (
                    <DocumentBodyNetPriceCell
                        value={getValue() ?? ""}
                        symbol={symbol}
                        prices={prices}
                        t={t}
                        canViewHistory={canViewSellingHistory}
                    />
                );
            },
        },
        {
            accessorKey: "total_net_price",
            header: t("app.documents.total_net_price", {defaultValue: "Totale netto"}),
            enableSorting: false,
            meta: isMobileViewport ? undefined : {pin: "right"},
            cell: ({row, getValue}) => {
                const symbol = row.original.currency_symbol ?? documentCurrencySymbol;
                const purchasePrice = row.original.purchase_price ?? row.original.product?.purchase_price;
                const netPrice = row.original.net_price ?? row.original.discount_price ?? row.original.unit_price;
                return (
                    <DocumentBodyTotalCell
                        total={getValue()}
                        symbol={symbol}
                        purchasePrice={purchasePrice}
                        netPrice={netPrice}
                        discountPrice={row.original.discount_price}
                        showMargin={resolvedShowMargin}
                    />
                );
            },
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ].filter(Boolean), [canCreateProduct, canEditProduct, canCreateProductRate, canManageDocumentConfirmedQuantity, canViewPurchaseHistory, canViewSellingHistory, confirmedQuantityEvents, documentCurrencySymbol, handleAssignProduct, handleCellChange, handleOpenAddRate, handleOpenConfirmedQuantity, handleOpenProductAdvancedSearch, handleOpenProductCreate, handleOpenProductEdit, handleOpenPurchaseHistory, handleSelectDimensions, isEditable, isExporter, isMobileViewport, resolvedCompanyId, resolvedShowMargin, t]);

    const columns = useMemo(() => {
        if (selectionColumn) {
            return [selectionColumn, ...dataColumns];
        }
        return dataColumns;
    }, [dataColumns, selectionColumn]);

    const canDisplayExportAction = Boolean(
        canExportDocument
        && resolvedCompanyId
        && documentId
        && resolvedDocumentType
        && (isEditable || isDocumentConfirmed)
    );
    const tableLoading = isLoading || isBodyLoading;
    const hasPersistedRows = rows.length > 0;
    const showTableLoader = tableLoading && !hasPersistedRows;
    const showTableOverlay = tableLoading && hasPersistedRows;

    // eslint-disable-next-line
    const errorMessages = useMemo(() => {
        const fallback = t("app.common.generic_error", {defaultValue: "Qualcosa è andato storto"});
        return [bodyError, updateError]
            .map((error) => {
                if (!error) {
                    return null;
                }
                return error.message ?? fallback;
            })
            .filter(Boolean);
    }, [bodyError, updateError, t]);

    const removeDetail = useMemo(() => {
        if (!rowPendingRemove || !resolvedCompanyId || !documentId) {
            return null;
        }
        const rowId = rowPendingRemove.id;
        if (!rowId) {
            return null;
        }
        const stringId = String(rowId);
        if (stringId.startsWith("new-") || stringId.startsWith("clone-")) {
            return null;
        }
        const defaultMessage = rowPendingRemove.name
            ? `Sei sicuro di voler eliminare "${rowPendingRemove.name}"?`
            : "Sei sicuro di voler eliminare questa riga?";
        return {
            title: t("app.documents.body_remove_title", {defaultValue: "Rimuovi riga documento"}),
            message: t("app.documents.body_remove_description", {
                defaultValue: defaultMessage,
                name: rowPendingRemove.name ?? "",
            }),
            endpoint: `company/${resolvedCompanyId}/document/${documentId}/body/${rowId}`,
        };
    }, [documentId, resolvedCompanyId, rowPendingRemove, t]);

    return (
        <>
            <ReportViewer
                isOpen={isExportReportOpen}
                reportDetail={exportReportUrl ? {reference: exportReportUrl} : false}
                title={t("app.document.preview_file", {defaultValue: "Anteprima documento"})}
                isActiveConfirm={false}
                zIndex={DOCUMENT_REPORT_VIEWER_Z_INDEX_CLASS}
                setIsOpen={handleToggleReportViewer}
                filename={document?.internal_reference ?? false}
            />

            <div className="w-full space-y-4">
                <div
                    ref={tableWrapperRef}
                    className="relative"
                    aria-busy={tableLoading}
                >
                    {showTableOverlay && (
                        <div className={`pointer-events-auto absolute inset-0 ${DOCUMENT_BODY_OVERLAY_Z_INDEX_CLASS} flex flex-col items-center justify-center gap-2 bg-white/70 backdrop-blur-sm`}>
                            <Loader />
                            <span className="text-sm font-medium text-gray-700">
                                {t("app.common.loading", {defaultValue: "Caricamento..."})}
                            </span>
                        </div>
                    )}
                        <DataTableEditable
                            listKey={tableListKey}
                            columns={columns}
                            data={filteredRows}
                            isLoading={showTableLoader}
                            fixedHeight={"h-[calc(100svh-215px)]"}
                            enableSearch
                            onSearchChange={handleSearch}
                            initialColumnVisibility={initialColumnVisibility}
                            toolbarActions={(
                                <>
                                    <DocumentBodyToolbar
                                        searchValue={searchTerm}
                                        searchResetToken={searchResetToken}
                                        massActions={massActionButtons}
                                        onAddRow={canAddDocumentRows ? handleAddRow : undefined}
                                        onOpenCatalog={isEditable && resolvedCompanyId && documentId && canAccessCatalog ? handleOpenCatalogPanel : undefined}
                                        onImport={isEditable && resolvedCompanyId && documentId && canImportDocumentProduct ? handleOpenImport : undefined}
                                        onExport={canDisplayExportAction ? handleOpenExport : undefined}
                                        exportLabel={t("app.common.export", {defaultValue: "Esporta"})}
                                        isExporting={isExporting}
                                        importLabel={t("app.common.import", {defaultValue: "Importa"})}
                                        addLabel={t("app.common.add", {defaultValue: "Aggiungi"})}
                                        catalogLabel={t("app.documents.open_catalog", {defaultValue: "Catalogo"})}
                                        onOpenReorder={isEditable && totalRowCount > 1 ? handleOpenReorderModal : undefined}
                                        reorderLabel={t("app.documents.body_reorder_rows", {defaultValue: "Riordina righe"})}
                                    />
                                </>
                            )}
                        enableColumnVisibility
                        enableColumnReorder
                        virtualizeRows={shouldVirtualizeRows}
                        virtualRowEstimateSize={VIRTUAL_ROW_ESTIMATE_SIZE}
                        virtualOverscan={VIRTUAL_ROW_OVERSCAN}
                        resolveRowStyling={(row) => {
                            if (!row || typeof row !== "object") {
                                return {};
                            }
                            const isHidden = Boolean(row.original?.hide);
                            if (!isHidden) {
                                return {};
                            }
                            return {
                                rowClassName: "bg-red-50",
                                cellClassName: "bg-red-50 text-red-700",
                                pinnedCellClassName: "bg-red-50 text-red-700",
                                cellHoverClassName: "group-hover:bg-red-100",
                                selectedCellClassName: "bg-red-200",
                            };
                        }}
                        emptyStateTitle={t("app.common.no_data", {defaultValue: "Nessun dato disponibile"})}
                        stickyHeader
                        stickyHeaderOffset={0}
                        stickyHeaderZIndex={DOCUMENT_TABLE_TOOLBAR_Z_INDEX}
                        isRowSelected={(row) => Boolean(selectedRows[row.original?.id])}
                    />
                </div>
                {isExportOpen && (
                    <DocumentBodyExportOptionsModal
                        isOpen={isExportOpen}
                        onClose={handleCloseExport}
                        onConfirm={handleConfirmExport}
                        includeSensitiveData={includeSensitiveData}
                        includeProductDataImages={includeProductDataImages}
                        onToggleSensitiveData={setIncludeSensitiveData}
                        onToggleProductDataImages={setIncludeProductDataImages}
                        canToggleSensitiveData={canIncludeSensitiveDocumentData}
                        isLoading={isExporting}
                        errorMessage={exportError}
                    />
                )}
                {isBulkPurchasePriceModalOpen && (
                    <DocumentBodyBulkPurchasePriceModal
                        isOpen={isBulkPurchasePriceModalOpen}
                        onClose={handleCloseBulkPurchasePriceModal}
                        onConfirm={handleConfirmBulkPurchasePrice}
                        isProcessing={isBulkPurchasePriceUpdating}
                        mode={bulkPurchasePriceScope}
                        onModeChange={setBulkPurchasePriceScope}
                        selectionCount={selectedRowIds.length}
                    />
                )}
                {batchConfirmDetail && (
                    <ConfirmPopup
                        isOpen={isBatchConfirmOpen}
                        setIsOpen={handleBatchConfirmToggle}
                        detail={{...batchConfirmDetail, endpoint: ""}}
                        onConfirmed={handleBatchConfirm}
                        isJustConfirm
                    />
                )}
                {isBulkConfirmQuantityConfirmOpen && (
                    <ConfirmPopup
                        isOpen={isBulkConfirmQuantityConfirmOpen}
                        setIsOpen={setIsBulkConfirmQuantityConfirmOpen}
                        detail={{...bulkConfirmQuantityConfirmDetail, endpoint: ""}}
                        onConfirmed={handleConfirmBulkConfirmQuantity}
                        isJustConfirm
                    />
                )}
                {removeDetail && (
                    <RemovePopup
                        isOpen={isRemoveOpen}
                        setIsOpen={setIsRemoveOpen}
                        detail={removeDetail}
                        setDetail={(payload) => {
                            deleteResponseRef.current = payload;
                        }}
                        onRemoveConfirmed={handleRemoveConfirmed}
                    />
                )}
                {isImportOpen && canImportDocumentProduct && (
                    <DocumentBodyImport
                        title={t("app.documents.body_import_title", {defaultValue: "Importa righe"})}
                        isOpen={isImportOpen}
                        setIsOpen={(nextOpen) => {
                            if (nextOpen) {
                                setIsImportOpen(true);
                                return;
                            }
                            handleCloseImport();
                        }}
                        importGroup={DOCUMENT_BODY_IMPORT_CONFIG.type}
                        importData={DOCUMENT_BODY_IMPORT_CONFIG}
                        onImportComplete={handleImportComplete}
                        importEntityId={documentId}
                    />
                )}
                {resolvedCompanyId && isCatalogPanelOpen && canAccessCatalog && (
                    <DocumentBodyCatalogPanel
                        isOpen={isCatalogPanelOpen}
                        onClose={handleCloseCatalogPanel}
                        onConfirm={handleCatalogBulkInsert}
                        companyId={resolvedCompanyId}
                        isSubmitting={isCatalogSubmitting}
                        currencySymbol={documentCurrencySymbol}
                    />
                )}
                {resolvedCompanyId && canCreateProduct && isAddProductOpen && (
                    <AddProduct
                        isOpen={isAddProductOpen}
                        setIsOpen={setIsAddProductOpen}
                        onSuccess={(product) => {
                            const targetRowId = addProductRowId;
                            setIsAddProductOpen(false);
                            const normalizedProduct = product && product.product ? product.product : product;
                            if (normalizedProduct && normalizedProduct.id && targetRowId) {
                                handleAssignProduct(targetRowId, normalizedProduct);
                            }
                            setAddProductRowId(null);
                        }}
                    />
                )}
                {isAddRateOpen && addRateProduct?.id && (
                    <FormPopup
                        title={t("app.catalog.add", {type: t("app.products.pricing_title")})}
                        isOpen={isAddRateOpen}
                        setIsOpen={(nextOpen) => {
                            if (!nextOpen) {
                                handleCloseAddRate();
                            }
                        }}
                    >
                        <AddFormPriceHistory
                            data={addRateDefaults}
                            exception={addRateError}
                            isLoading={isAddRateSubmitting}
                            onSubmitData={handleAddRateSubmit}
                        />
                    </FormPopup>
                )}
                {isPurchaseHistoryOpen && purchaseHistoryRow && (
                    <DocumentBodyPurchasePriceHistoryPopup
                        isOpen={isPurchaseHistoryOpen}
                        onClose={handleClosePurchaseHistory}
                        product={purchaseHistoryRow.product ?? null}
                        purchasePrice={purchaseHistoryRow.purchase_price ?? purchaseHistoryRow.product?.purchase_price ?? ""}
                        t={t}
                        suffix={purchaseHistoryRow.currency_symbol ?? documentCurrencySymbol}
                        listPrice={purchaseHistoryRow.list_price ?? purchaseHistoryRow.product?.list_price ?? ""}
                        listPriceDiscounts={purchaseHistoryRow.list_price_discounts ?? purchaseHistoryRow.product?.list_price_discounts ?? []}
                        onListPriceCommit={handlePurchaseHistoryListPriceCommit}
                        onListPriceDiscountsCommit={handlePurchaseHistoryDiscountsCommit}
                        isEditable={isEditable}
                        canShowCatalogAction={canCreateProductRate && Boolean(purchaseHistoryRow.product?.id)}
                        onAddCatalog={handlePurchaseHistoryAddCatalog}
                        canViewHistory={canViewPurchaseHistory && Boolean(purchaseHistoryRow.product?.id)}
                    />
                )}
                {resolvedCompanyId && isProductAdvancedSearchOpen && (
                    <DocumentBodyProductAdvancedSearch
                        isOpen={isProductAdvancedSearchOpen}
                        onClose={handleToggleAdvancedSearch}
                        onSelect={handleAdvancedSearchSelect}
                        companyId={resolvedCompanyId}
                        excludeProductIds={assignedProductIds}
                    />
                )}
                {isReorderModalOpen && (
                    <DocumentBodyReorderModal
                        isOpen={isReorderModalOpen}
                        rows={rows}
                        onRequestClose={handleCloseReorderModal}
                        onConfirm={handleConfirmReorderModal}
                        isSubmitting={isReorderSubmitting}
                    />
                )}
                {isConfirmedQuantityOpen && confirmedQuantityRowId && resolvedCompanyId && documentId && (
                    <DocumentBodyConfirmedQuantityModal
                        isOpen={isConfirmedQuantityOpen}
                        onClose={handleCloseConfirmedQuantity}
                        companyId={resolvedCompanyId}
                        documentId={documentId}
                        bodyId={confirmedQuantityRowId}
                        onEventsChange={handleModalConfirmedEventsChange}
                        maxQuantity={confirmedQuantityRowMax}
                        initialQuantityTotal={confirmedQuantityRowExistingTotal}
                    />
                )}
                {resolvedCompanyId && canEditProduct && isEditProductOpen && (
                    <DocumentProductEditPanel
                        isOpen={isEditProductOpen}
                        productId={editProductId}
                        companyId={resolvedCompanyId}
                        onClose={handleCloseProductEdit}
                        onProductUpdated={handleProductEditRefresh}
                        t={t}
                    />
                )}
            </div>
        </>
    );
};

DocumentBody.propTypes = {
    document: PropTypes.object,
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    documentType: PropTypes.string,
    isLoading: PropTypes.bool,
    importRefreshKey: PropTypes.number,
    onImportQueued: PropTypes.func,
    showMargin: PropTypes.bool,
    onDocumentTotalsRefresh: PropTypes.func,
    canEdit: PropTypes.bool,
    stickyToolbarOffset: PropTypes.number,
    enableStickyToolbar: PropTypes.bool,
    externalConfirmedQuantityRequest: PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        event: PropTypes.object,
    }),
    onConfirmedQuantityEventsChange: PropTypes.func,
};

DocumentBody.defaultProps = {
    document: null,
    companyId: null,
    documentId: null,
    documentType: null,
    isLoading: false,
    importRefreshKey: 0,
    onImportQueued: null,
    showMargin: true,
    onDocumentTotalsRefresh: null,
    canEdit: true,
    stickyToolbarOffset: 0,
    enableStickyToolbar: true,
    externalConfirmedQuantityRequest: null,
    onConfirmedQuantityEventsChange: undefined,
};

export {DOCUMENT_BODY_IMPORT_TYPE};
export default React.memo(DocumentBody);
